/**
 * LessonReview — Lesson Footage Upload + AI Competency Review.
 *
 * Single-page UX: upload zone → live processing pipeline → results view.
 *
 * Pipeline stages shown to the user:
 *   queued → extracting_audio → transcribing → analysing → complete
 *
 * Once complete, surfaces:
 *   - Demonstrated / Missed competency chips (17 DVSA Part-3)
 *   - Improvement tip (1-line LLM summary)
 *   - Top 3 next focus → links to Mastery Builder skills
 *   - Transcript excerpt (collapsible)
 *   - History list of previous reviews
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import { MobileDialog } from "../components/mobile";
import axios from "axios";
import {
  Upload, Loader2, FileVideo, ArrowLeft, CheckCircle2, AlertCircle,
  Sparkles, ChevronRight, Clock, Trash2, Play, ShieldCheck, X, Target, Crown,
  RotateCw, Download, Film, Share2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useTruthState, TRUTH_STATE_NEEDS_INPUT } from "../hooks/useTruthState";
import { TruthReceipt } from "../components/TruthReceipt";
import { trackFunnel } from "../utils/analytics";
import { MEDIA_FEATURE_ENABLED } from "../lib/mediaFeatureFlag";
import UploadPricingModal from "../components/UploadPricingModal";
import ClipReadyPanel from "../components/instructor/ClipReadyPanel";
import CoreJourneyMantra from "../components/CoreJourneyMantra";
import AIAnalysisProgress from "../components/AIAnalysisProgress";
import BypassModeBanner from "../components/admin/BypassModeBanner";
import { useAuth } from "../context/AuthContext";
import { chunkedUpload, formatBytes, AuthExpiredError, UploadPausedError } from "../lib/chunkedUpload";
import { adaptiveUpload, isCloudUploadEnabled } from "../lib/uploadAdapter";
import {
  shouldCompress, compressVideo, isCompressionFeatureEnabled,
} from "../lib/videoCompressor";
import {
  savePendingUpload, loadPendingUpload, clearPendingUpload,
  fileMatchesPendingRecord, compareFileToPendingRecordWithDuration,
  probeVideoDurationSeconds, describeMismatch,
} from "../lib/uploadPersistence";
import {
  isIphoneLikely, isLikelyFromPhotosPicker, probeFileReadability,
  shouldSuggestFilesPicker, logUploadEvent,
} from "../lib/iphoneUploadGuard";
import {
  rememberRecentReview, loadRecentReview, clearRecentReview,
  findRecentInFlightReview, findRecoveryByFile, isTerminalStatus,
} from "../lib/recentReviewRecovery";
import UploadProgressPanel from "../components/upload/UploadProgressPanel";
import UploadDebugPanel from "../components/upload/UploadDebugPanel";
// E3 (May 14, 2026 — Eagle-Eyed Operational Review §3):
// UploadRecoveryBanner and ResumeSessionBanner retired. Active
// Operation pill + /operations history cover the same recovery
// case via server-side SSOT. The supporting localStorage helpers
// (savePendingUpload/loadPendingUpload/findRecoveryByFile/etc.)
// remain — they still guard the in-page "same file re-pick"
// detection logic that prevents iPhone Photos picker mistakes.
import PendingReviewQueue from "../components/lesson_review/PendingReviewQueue";
import PendingAnalysisRail from "../components/lesson_review/PendingAnalysisRail";
import AiReviewTierPicker from "../components/lesson_review/AiReviewTierPicker";
import PreUploadDetailsForm from "../components/lesson_review/PreUploadDetailsForm";
import DualScorePanel from "../components/lesson_review/DualScorePanel";
import InsufficientEvidencePanel from "../components/lesson_review/InsufficientEvidencePanel";
import InstructorConfirmationPill from "../components/learner/InstructorConfirmationPill";
import ClipBasketModal from "../components/instructor/ClipBasketModal";
import ClipBasketV2 from "../components/upload/ClipBasketV2";
import useBasketAutoReopen from "../hooks/useBasketAutoReopen";
import { legacyUploadCompatEnabled } from "../lib/legacyUploadCompat";
import usePickerHandoff from "../hooks/usePickerHandoff";
import PickerRecoveryCallout from "../components/upload/PickerRecoveryCallout";
import { useSessionKeepAlive } from "../hooks/useSessionKeepAlive";
import useWakeLock from "../hooks/useWakeLock";

const API = process.env.REACT_APP_BACKEND_URL;
// 6 GiB hard cap (Feb 7 2026 — raised from 2 GiB). Files >1 GiB go
// through the chunked upload pipeline (resume + retry per chunk +
// integrity check on assembly). The cap mirrors the backend
// `MAX_TOTAL_BYTES` in chunked_upload.py.
const MAX_BYTES = 6 * 1024 * 1024 * 1024;
const LARGE_FILE_WARNING_BYTES = 1 * 1024 * 1024 * 1024;

// Accept attribute — MIME-pattern form rather than file extensions.
//
// iPhone Chrome regression fix (May 14, 2026 — production bug
// report: users stuck inside Photos watching a spinner before the
// File handoff). When iOS sees an extension-based accept list
// (".mp4,.mov,.m4v,...") it forces the slow Photos "export +
// verify" path — full iCloud download, sometimes HEVC→H.264
// transcode in Photos itself. With MIME patterns (video/*, audio/*)
// iOS uses the native PHPicker fast path: the user picks, iOS
// returns a PHAsset reference promptly, and the iCloud download
// happens in the background with a small "Preparing…" indicator
// rather than holding the user inside Photos. The "*" suffix
// matches every video / audio container type so users can't pick
// the wrong file type, but we keep the MIME-pattern shape iOS
// optimises for.
const ACCEPTED = "video/*,audio/*";

const STAGE_LABELS = {
  awaiting_payment: { label: "Awaiting payment",  pct: 0,   icon: Clock },
  paid:             { label: "Paid · queuing",    pct: 3,   icon: CheckCircle2 },
  // Brief assembly state — UI shows this while the server reassembles
  // the chunked upload + runs ffprobe (Feb 7 2026).
  finalizing:       { label: "Finalizing",        pct: 4,   icon: Loader2 },
  queued:           { label: "Queued",            pct: 5,   icon: Clock },
  extracting_audio: { label: "Extracting audio",  pct: 25,  icon: FileVideo },
  transcribing:     { label: "Transcribing",      pct: 55,  icon: Sparkles },
  // Auto-clip pipeline (May 5 2026) — pupil-paid focused requests only.
  detecting_clips:  { label: "Finding key moments", pct: 65, icon: Sparkles },
  extracting_clips: { label: "Preparing clip",     pct: 75,  icon: FileVideo },
  clip_ready:       { label: "Clip ready · review", pct: 80, icon: Sparkles },
  // Both legacy ("analysing") and friendlier ("grading") map here.
  analysing:        { label: "Grading",           pct: 85,  icon: Target },
  grading:          { label: "Grading",           pct: 85,  icon: Target },
  processing:       { label: "Processing",        pct: 70,  icon: Sparkles },
  complete:         { label: "Complete",          pct: 100, icon: CheckCircle2 },
  completed:        { label: "Complete",          pct: 100, icon: CheckCircle2 },
  failed:           { label: "Failed",            pct: 100, icon: AlertCircle },
};

export default function LessonReview() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const { user } = useAuth();
  // Batch P1 · Recording Truth-State — single canonical async
  // receipt for every Recording/AI-review action. Bespoke `toast.*`
  // calls have been replaced by `truth.setState(...)` in migrated
  // paths. AI-review submissions that are ACCEPTED but not yet
  // completed remain in the `waiting` state, per the doctrine that
  // "accepted != completed".
  const truth = useTruthState();

  // Batch P1 · Recording Truth-State — calm auto-clear for terminal
  // successes. Failed / needs_input / waiting states linger until
  // the user dismisses or retries.
  useEffect(() => {
    if (truth.state === "completed") {
      const t = setTimeout(() => truth.setState(null), 1200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [truth]);
  const [active, setActive] = useState(null);   // current/in-progress review doc
  const [history, setHistory] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  // Perceived Speed System (Feb 7 2026) — drive the UploadProgressPanel
  // through the spec'd phases. "idle" hides the panel entirely.
  const [uploadPhase, setUploadPhase] = useState("idle");
  const [uploadFile, setUploadFile] = useState(null);
  // Compression progress (0-100) reported by ffmpeg.wasm. Drives the
  // "Optimising video for faster upload…" stage when the feature flag
  // is on AND the file passes shouldCompress() gates.
  const [optimisePct, setOptimisePct] = useState(0);
  // P0 (Feb 7 2026 speed) — live mbps from chunkedUpload helper.
  // Drives "Your connection is slow" copy when < 2 Mbps for 8s+.
  const [uploadMbps, setUploadMbps] = useState(null);
  // P0 (Feb 7 2026) pause/resume — when chunkedUpload throws
  // UploadPausedError, store the upload_id + the original File so the
  // user can tap "Resume upload" and pick up exactly where they left
  // off. Cleared on success / cancel / new file pick.
  const [pausedUpload, setPausedUpload] = useState(null);
  // P0 (Feb 7 2026) background-safe uploads — when the app was killed
  // mid-upload (iOS tab eviction, reload, crash), we re-read a record
  // from localStorage on mount so the user can finish the upload by
  // re-picking the same file. `pendingRecord` holds the offer until
  // the user either re-picks (continues) or dismisses (clears).
  const [pendingRecord, setPendingRecord] = useState(null);
  // Feb 8, 2026 — visible diagnostic surface. Populated on every
  // throttled progress flush from chunkedUpload(). Rendered only when
  // `localStorage.lr_debug_uploads === "true"` (operator can flip on
  // for one preview test without redeploying). Drives the on-screen
  // debug card so the user can read upload_id / failed chunk / HTTP
  // status / retry count straight off their phone.
  const [uploadDebug, setUploadDebug] = useState(null);
  const [open, setOpen] = useState(null);       // detail view review id
  const [openDoc, setOpenDoc] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [pricingQuote, setPricingQuote] = useState(null);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [founderFree, setFounderFree] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // AI Review Tier — null until user (or server default) picks one;
  // forwarded with every chunked-complete / cloud-complete payload.
  const [aiReviewTier, setAiReviewTier] = useState(null);

  // Pre-Upload human-readable details (Feb 9, 2026) — operator
  // directive: stop showing cards/chips with cryptic camera filenames
  // like "NO20260503-164104-000572.mov". After a fresh file pick
  // passes preflight, we hold the file here and surface a small form
  // (Title / Camera label / Notes) BEFORE the upload starts. The
  // user-supplied values ride on `/chunked-complete` and
  // `/cloud-complete` so the resulting `lesson_reviews` row carries
  // logical names everywhere on the platform (history, processing
  // chip, completed cards). Resume paths intentionally bypass the
  // form — the original session's details (or null fallback) ride on
  // through the existing flow.
  const [pendingDetailsFile, setPendingDetailsFile] = useState(null);
  const [uploadDetails, setUploadDetails] = useState(null);

  // Single vs Multi-clip lesson mode (Feb 9, 2026) — operator
  // directive: "I need the basket visible and usable from my
  // self-review flow." Default to "single" so the existing UX is
  // preserved; the toggle reveals the multi-clip basket entry.
  const [lessonReviewMode, setLessonReviewMode] = useState("single");
  const [basketOpen, setBasketOpen] = useState(false);
  // When set, ClipBasketModal skips the /create call and reopens
  // the existing basket — the resume path. Cleared on close.
  const [resumeBasketId, setResumeBasketId] = useState(null);

  // P0 May 21, 2026 — Upload Basket v2 cutover. When the feature
  // flag is on, the legacy ClipBasketModal is fully retired from
  // the normal preview flow and replaced with ClipBasketV2 — a
  // clean isolated flow with its own local-draft authority,
  // backend sync and review handoff. The legacy modal remains in
  // the file purely as an emergency rollback (flag off). v2 does
  // not share state, beacon recovery, or hydration with old basket.
  const useV2Basket = process.env.REACT_APP_UPLOAD_BASKET_V2 === "true";

  // P0 May 20, 2026 — iOS Photos picker handoff recovery. If the
  // basket modal unmounts during the Photos picker (WKWebView
  // suspend-and-evict), this hook reads the picker beacon on
  // mount/pageshow/focus and re-opens the basket with the same
  // basket_id so the user is never dumped back to this start
  // screen with no state. See /app/frontend/src/lib/pickerBeacon.js.
  //
  // P0 May 21, 2026 — disabled when Upload Basket v2 is active.
  // v2 uses its own local-draft persistence (clipBasketDraft.js)
  // and must not reuse old beacon-driven hydration overwrite.
  useBasketAutoReopen({
    basketOpen,
    setBasketOpen,
    setResumeBasketId,
    source: "lesson_review",
    enabled: !useV2Basket,
  });

  // P0 May 20, 2026 — universal picker handoff recovery for the
  // single-clip inline picker path. The multi-clip basket path
  // already has its own server-side basket recovery via the beacon;
  // the single-clip path uses the universal hook to detect when
  // the Photos handoff was attempted but onChange never fired.
  // Surfaces a visible recoverable callout so the user is never
  // dumped back to a dead start screen.
  //
  // P0 May 22, 2026 — disabled when Upload Basket v2 is active.
  // The hook registers global pageshow/focus/visibilitychange
  // listeners that re-render LessonReview on Photos return; the
  // re-render storm lands in the exact window where V2's onChange
  // is firing and can unmount the V2 modal. V2 owns its own
  // intake recovery via MobilePhotosIntake's watchdog.
  const picker = usePickerHandoff({
    source: "lesson_review_single",
    hasStagedFiles: () => uploadPhase !== "idle",
    enabled: !useV2Basket,
  });

  // Feb 8, 2026 — phase-1 Stage 2 rollout. When the adapter routes
  // an upload through direct-to-R2 we surface a small reassuring
  // badge ("Direct cloud upload active — large videos supported")
  // so the user understands large files are now first-class. Reset
  // to null when the upload finishes / errors / a new file is picked.
  const [activeUploadStage, setActiveUploadStage] = useState(null);
  // P0 May 20, 2026 — `fileInputRef` is now aliased to
  // `picker.inputRef` so every existing `.click()` call in this
  // file (retry buttons, last-resort re-pick) ALSO writes a
  // picker beacon + emits a diagnostic event, without having to
  // refactor every caller. Same ref, same DOM node, plus
  // observability.
  const fileInputRef = picker.inputRef;
  const pollRef = useRef(null);
  // Feb 8, 2026 — iPhone-recovery hardening. Duration is probed from
  // the picked File at pick-time, before any backend round-trip, and
  // threaded into every savePendingUpload via this ref so the
  // recovery banner can always say "Looking for a 19:03 video".
  const pickedDurationRef = useRef(null);

  // P0 (Feb 7 2026) — keep the session alive while a chunked upload is
  // running OR while the active review is still being processed by the
  // backend. Stops the moment the review hits a terminal state or the
  // user navigates away. Manual logout is respected automatically.
  const isProcessingActive = !!(
    active &&
    !["complete", "completed", "failed", "awaiting_payment"].includes(
      (active.status || "").toLowerCase(),
    )
  );
  useSessionKeepAlive(uploading || isProcessingActive, { label: "lesson_review" });

  // Operational continuity (May 14, 2026 — upload continuity fix):
  // Claim a screen wake-lock for the duration of any active upload
  // (and also while the AI pipeline is still processing — releasing
  // immediately on upload end would let the screen sleep mid-grading
  // on an iPhone left on the desk). The hook returns
  // `fallbackWarning` text when the browser doesn't support Wake Lock
  // (Safari < 16.4, in-app browsers, Firefox); we render that text
  // truthfully near the upload card so the user is never told the
  // screen will stay awake when it actually won't.
  const wakeLockActive = uploading || isProcessingActive;
  const { fallbackWarning: wakeLockFallback } = useWakeLock(
    wakeLockActive,
    { label: "lesson_review_upload" },
  );

  // Founder-free status for admin toggle
  useEffect(() => {
    if (!MEDIA_FEATURE_ENABLED) return;       // Media quarantine — page is route-quarantined.
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/upload-pricing/founder-free-mode`, { withCredentials: true });
        setIsAdmin(!!data.is_admin);
        setFounderFree(!!data.enabled);
      } catch { /* silent */ }
    })();
  }, []);

  // Phase 4 / Phase F (Feb 13, 2026) — Track A pupil deep-link.
  // Pupils click `/lesson-review?id=<id>` from their Passport
  // history / inbox notifications. The page never had a query
  // loader before this — add it. Loads the doc as `active` so the
  // existing done-state render path picks it up (and now also
  // surfaces the InstructorConfirmationPill).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reviewId = params.get("id");
    if (!reviewId) return;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/lesson-review/${encodeURIComponent(reviewId)}`,
          { withCredentials: true },
        );
        if (data?.id) setActive(data);
      } catch {
        // Soft-fail; the empty-state UI still loads underneath.
      }
    })();
  }, []);

  // Handle Stripe redirect — finalise payment + kick off polling
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("upload_session");
    const cancelled = params.get("upload_cancelled");
    if (cancelled) {
      truth.setState("waiting", "Checkout cancelled. Your upload is still ready — choose an option when you are.");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    if (!sid) return;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/upload-pricing/status/${encodeURIComponent(sid)}`,
          { withCredentials: true },
        );
        if (data.status === "paid" && data.review) {
          truth.setState("waiting", "Paid — AI is analysing your lesson now (this may take a few minutes).");
          setActive(data.review);
        } else {
          truth.setState("waiting", "Payment still processing — we'll pick it up shortly.");
        }
      } catch {
        truth.setState("failed", "Couldn't finalise payment — please contact support if charged");
      } finally {
        window.history.replaceState({}, "", window.location.pathname);
      }
    })();
  }, []);

  // P0 (Feb 7 2026) — background-safe uploads.
  // On mount, check localStorage for an in-flight upload from this
  // user (tab was backgrounded / killed / reloaded). If the server's
  // session is still alive and resumable, surface a "re-pick your
  // file to continue" offer. The user re-picking the same file
  // resumes from the exact last successful chunk.
  //
  // May 14, 2026 (operator audit — upload→AI handoff contract):
  // if the user has an AI review for this session already in flight,
  // the upload row is BY DEFINITION historical truth. Clear any
  // stale localStorage pendingRecord without surfacing the recovery
  // banner — "Resume upload" + "AI analysis in progress" must
  // NEVER appear together.
  useEffect(() => {
    if (!user) return;
    const rec = loadPendingUpload(user._id || user.id || user.email, "lesson_review");
    if (!rec) return;
    // Suppress + scrub when AI review has already taken over.
    if (active && !["failed", "cancelled"].includes(active.status)) {
      // eslint-disable-next-line no-console
      console.info("[upload-continuity] localStorage_purged_for_active_review", {
        review_id: active.id,
        review_status: active.status,
        upload_id: rec.uploadId,
      });
      clearPendingUpload(user._id || user.id || user.email, "lesson_review");
      setPendingRecord(null);
      setPausedUpload(null);
      return;
    }
    (async () => {
      try {
        // Probe the server to confirm the session is still resumable.
        // /status is tolerant of any state (including failed) and
        // self-heals phantoms, so a 200 means genuinely resumable.
        const { data } = await axios.get(
          `${API}/api/uploads/chunked/${rec.uploadId}/status`,
          { withCredentials: true },
        );
        // If server says it's already complete or cancelled, drop the
        // record — nothing to resume.
        if (["complete", "cancelled", "expired"].includes(data?.status)) {
          clearPendingUpload(user._id || user.id || user.email, "lesson_review");
          return;
        }
        // Server truth wins — update counts from /status.
        const received = (data?.received_chunks || []).length;
        setPendingRecord({
          ...rec,
          lastSuccessfulChunk: received ? received - 1 : -1,
          serverReceived: received,
          serverTotal: data?.total_chunks || rec.totalChunks,
        });
      } catch (err) {
        // 404 / 410 / auth-fail → the session is no longer usable.
        const status = err?.response?.status;
        if (status === 404 || status === 410) {
          clearPendingUpload(user._id || user.id || user.email, "lesson_review");
        }
        // Auth errors — leave the record; user may log back in.
      }
    })();
  }, [user]);

  // E.M.S. (May 14, 2026 — operator audit): when an AI review goes
  // pipeline-active, retire EVERY upload-recovery surface in the page
  // immediately — never let "Resume upload" coexist with
  // "AI analysis in progress". Belt-and-braces over the mount-effect
  // guard above; this catches the case where the AI review row
  // appears asynchronously while the page is already open.
  useEffect(() => {
    if (!active || !user) return;
    const PIPELINE_ACTIVE = new Set([
      "queued", "transcribing", "grading",
      "extracting_clips", "preparing_release", "complete",
    ]);
    if (!PIPELINE_ACTIVE.has(active.status)) return;
    const uid = user._id || user.id || user.email;
    // eslint-disable-next-line no-console
    console.info("[upload-continuity] ai_review_supersedes_upload", {
      review_id: active.id,
      review_status: active.status,
    });
    clearPendingUpload(uid, "lesson_review");
    setPendingRecord(null);
    setPausedUpload(null);
  }, [active, user]);

  // P0 (Feb 7 2026) — persist on `visibilitychange` so iOS tab
  // eviction doesn't lose the pointer to an in-flight upload.
  //
  // LEGACY_DECOMMISSIONED (May 22, 2026): the listener is gated by
  // legacyUploadCompatEnabled(). Default OFF means no global pagehide
  // or visibilitychange listener is registered for upload state.
  useEffect(() => {
    if (!user) return undefined;
    if (!legacyUploadCompatEnabled()) return undefined;
    const uid = user._id || user.id || user.email;
    const persist = () => {
      // Only persist when there's an active upload or a paused one.
      if (pausedUpload?.uploadId) {
        savePendingUpload({
          userId: uid,
          purpose: "lesson_review",
          uploadId: pausedUpload.uploadId,
          fileName: pausedUpload.file?.name,
          fileSize: pausedUpload.file?.size,
          fileLastModified: pausedUpload.file?.lastModified,
          totalChunks: pausedUpload.totalChunks,
          lastSuccessfulChunk: pausedUpload.lastSuccessful,
          reason:
            pausedUpload.reason === "auth" ? "paused-auth" : "paused-network",
        });
      }
    };
    const onVis = () => {
      if (document.visibilityState === "hidden") persist();
      logUploadEvent("lifecycle.visibilitychange", {
        hidden: document.visibilityState === "hidden",
        upload_phase: uploadPhase,
        has_paused: !!pausedUpload?.uploadId,
      });
    };
    const onPageHide = (e) => {
      logUploadEvent("lifecycle.pagehide", {
        persisted: !!e?.persisted,
        upload_phase: uploadPhase,
        has_paused: !!pausedUpload?.uploadId,
      });
      persist();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [user, pausedUpload, uploadPhase]);

  const toggleFounderFree = async () => {
    try {
      const { data } = await axios.post(
        `${API}/api/upload-pricing/founder-free-mode`,
        { enabled: !founderFree },
        { withCredentials: true },
      );
      setFounderFree(!!data.enabled);
      truth.setState("completed", data.enabled ? "Founder Free Mode ON" : "Founder Free Mode OFF");
    } catch (e) {
      truth.setState("failed", e.response?.data?.detail || "Couldn't toggle");
    }
  };

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/lesson-review/list`, { withCredentials: true });
      setHistory(data.items || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // ── P0 Feb 9, 2026 — Resume Session banner ─────────────────────
  // Operator: "logout/login was losing basket/session state". The
  // backend always preserved the data — we just had no UI to surface
  // it. On mount, fetch:
  //   • incomplete clip baskets (clips_staged | scanning | etc.)
  //   • interrupted reviews (queued | transcribing | grading | …)
  // and show a banner with a one-tap Resume button.
  //
  // Dismiss persists in sessionStorage (NOT localStorage) so the
  // banner reappears in a NEW tab/session if the user really wants
  // to come back.
  const [resumeBasket, setResumeBasket] = useState(null);
  const [resumeReview, setResumeReview] = useState(null);
  const [resumeDismissed, setResumeDismissed] = useState(() => {
    try {
      return new Set(
        JSON.parse(sessionStorage.getItem("ndg_resume_dismissed") || "[]"),
      );
    } catch { return new Set(); }
  });

  const persistResumeDismissed = useCallback((next) => {
    setResumeDismissed(next);
    try {
      sessionStorage.setItem(
        "ndg_resume_dismissed", JSON.stringify(Array.from(next)),
      );
    } catch { /* silent */ }
  }, []);

  // Server-truth refetch for the resume banner data. Hoisted so we
  // can fire it on visibilitychange / focus / pageshow — the user
  // should never have to manually browser-refresh to see fresh
  // basket/review state. (Feb 11, 2026 mobile stability sprint.)
  const refreshResumeState = useCallback(async (reason = "mount") => {
    if (!user) return;
    try {
      const [b, r] = await Promise.all([
        axios.get(`${API}/api/clip-basket/list`, { withCredentials: true })
          .catch(() => ({ data: { baskets: [] } })),
        axios.get(`${API}/api/lesson-review/interrupted`, { withCredentials: true })
          .catch(() => ({ data: { items: [] } })),
      ]);
      const baskets = b?.data?.baskets || [];
      const reviews = r?.data?.items || [];
      // iter_158d (Feb 13, 2026): backend now returns
      // `is_resumable_by_user`. Honour it to avoid surfacing
      // baskets the server is processing (those flow via
      // ProcessingReviewChip, not the resume banner).
      const firstBasket = baskets.find(
        (x) => x.is_resumable_by_user && !resumeDismissed.has(x.id),
      ) || null;
      const firstReview = reviews.find((x) => !resumeDismissed.has(x.id)) || null;
      setResumeBasket(firstBasket);
      setResumeReview(firstReview);
      // eslint-disable-next-line no-console
      console.debug("[lr-refresh] state_updated", {
        reason,
        baskets: baskets.length,
        reviews: reviews.length,
        surface: "LessonReview",
      });
      if (firstBasket) {
        // eslint-disable-next-line no-console
        console.debug("[lr-mobile] resume_session_visible", {
          basket_id: firstBasket.id,
          clip_count: firstBasket.staged_clip_count || 0,
        });
        // eslint-disable-next-line no-console
        console.debug("[lr-recovery] recovered_from_server_state", {
          surface: "LessonReview",
          basket_id: firstBasket.id,
        });
      }
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, resumeDismissed]);

  useEffect(() => {
    if (!user) return undefined;
    // LEGACY_DECOMMISSIONED (May 22, 2026): the resume-state refetch
    // listeners (visibilitychange / focus / pageshow) silently
    // resurrected basket + interrupted-review banners on every
    // Photos return — root cause of the iPhone upload ghost. They
    // are now armed ONLY when REACT_APP_LEGACY_UPLOAD_COMPAT=true.
    if (!legacyUploadCompatEnabled()) return undefined;
    // Even with legacy compat ON, suppress the refetch storm while
    // V2 is open — V2 owns its own state authority.
    if (useV2Basket && basketOpen) return undefined;
    refreshResumeState("mount");
    // Visibility + focus + pageshow → immediate refetch. iOS bfcache
    // restores the page without firing visibilitychange, so pageshow
    // is a critical extra hook.
    const onVisible = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      // eslint-disable-next-line no-console
      console.debug("[lr-refresh] focus_refetch", {
        trigger: "visibility", surface: "LessonReview",
      });
      refreshResumeState("visibility");
    };
    const onFocus = () => {
      // eslint-disable-next-line no-console
      console.debug("[lr-refresh] focus_refetch", {
        trigger: "focus", surface: "LessonReview",
      });
      refreshResumeState("focus");
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    window.addEventListener("pageshow", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pageshow", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, refreshResumeState, useV2Basket, basketOpen]);

  // Mobile viewport marker — emit once per mount.
  // May 14, 2026 (production bug fix): when on the LessonReview surface,
  // toggle a body class so html/body get the dark "page" background.
  // Previously the outer container claimed `min-height: 100dvh` AND the
  // FounderModeBanner (sticky top-0 z-60, ~3rem) sat above it,
  // pushing the page off-screen on iPhone Chrome as the URL bar
  // collapsed. Letting body own the dvh + dark bg lets the founder
  // banner share the viewport without overflowing.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.add("lr-dark-bg");
    // eslint-disable-next-line no-console
    console.debug("[lr-mobile] viewport_stable", {
      uses_dvh: true, surface: "LessonReview",
    });
    return () => { document.body.classList.remove("lr-dark-bg"); };
  }, []);

  const onResumeBasket = useCallback((basket) => {
    setLessonReviewMode("multi");
    setResumeBasketId(basket.id);
    setBasketOpen(true);
  }, []);

  const onResumeReview = useCallback((review) => {
    setActive(review);
  }, []);

  const onDismissResume = useCallback((target) => {
    const next = new Set(resumeDismissed);
    next.add(target.id);
    persistResumeDismissed(next);
    if (target.kind === "basket") setResumeBasket(null);
    else setResumeReview(null);
  }, [resumeDismissed, persistResumeDismissed]);

  // ── P0 (Feb 8, 2026) — post-upload recovery on mount. ─────────
  // If the user got dumped back here AFTER an upload completed
  // server-side (iOS Safari background eviction during the 2.7s
  // finalising hold, or a tab reload), rehydrate `active` from
  // either:
  //   (a) the sticky localStorage pointer (rememberRecentReview),
  //       which was written the moment chunked-complete returned —
  //       authoritative if it survives.
  //   (b) the freshest non-terminal review created in the last 15
  //       minutes — falls back when WebKit purged localStorage.
  //
  // Either way the user lands on the active review surface instead
  // of a blank upload zone. Polling effect takes over from there.
  const [recoveryReview, setRecoveryReview] = useState(null);
  useEffect(() => {
    if (!user) return;
    if (active) return;          // already on an active review
    const uid = user._id || user.id || user.email;
    let cancelled = false;

    (async () => {
      // Vector A — localStorage pointer.
      const pointer = loadRecentReview(uid);
      logUploadEvent("recovery.pointer_check", {
        has_pointer: !!pointer,
        review_id: pointer?.reviewId || null,
        status: pointer?.status || null,
        age_seconds: pointer ? Math.round((Date.now() - pointer.ts) / 1000) : null,
      });
      if (pointer) {
        try {
          const { data } = await axios.get(
            `${API}/api/lesson-review/${pointer.reviewId}`,
            { withCredentials: true },
          );
          if (cancelled || !data || !data.id) return;
          if (isTerminalStatus(data.status)) {
            // Terminal — no need to surface a recovery card; the
            // user can still see it in their history list below.
            clearRecentReview(uid);
            logUploadEvent("recovery.pointer_terminal_cleared", {
              review_id: data.id, status: data.status,
            });
            return;
          }
          // Non-terminal — surface as the active review so the user
          // sees their processing card immediately, not a banner.
          setActive(data);
          logUploadEvent("upload.recovered_via_localstorage", {
            review_id: data.id, status: data.status,
          });
          return;
        } catch (err) {
          // 404 / 403 — pointer is stale, fall through to history scan.
          logUploadEvent("recovery.pointer_fetch_failed", {
            review_id: pointer.reviewId,
            http_status: err?.response?.status ?? null,
          });
          clearRecentReview(uid);
        }
      }

      // Vector B — history scan (only when localStorage didn't give
      // us a usable pointer). Surfaces a banner rather than auto-
      // setting active so the user explicitly opts in.
      if (cancelled) return;
      const recent = findRecentInFlightReview(history);
      if (recent) {
        setRecoveryReview(recent);
        logUploadEvent("upload.recovery_banner_offered", {
          review_id: recent.id, status: recent.status,
          source: "history_scan",
        });
      } else {
        logUploadEvent("recovery.no_match", {
          history_length: Array.isArray(history) ? history.length : 0,
        });
      }
    })();

    return () => { cancelled = true; };
    // Intentionally depend on `history` so the effect re-runs once
    // the list resolves. `active`/`user` re-trigger when the user
    // signs in or dismisses an active review.
  }, [user, active, history]);

  // Clear the sticky pointer when the active review reaches a
  // terminal state — keeps localStorage tidy.
  useEffect(() => {
    if (!user || !active) return;
    if (isTerminalStatus(active.status)) {
      clearRecentReview(user._id || user.id || user.email);
    }
  }, [user, active?.status]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Funnel: page viewed
  useEffect(() => { trackFunnel("practice_review", "viewed"); }, []);

  // Poll the active review until it's complete or fails.
  useEffect(() => {
    if (!active || ["awaiting_payment", "complete", "completed", "failed"].includes(active.status) || (active.status === "awaiting_payment" && !active.paid)) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/api/lesson-review/${active.id}`, { withCredentials: true });
        setActive(data);
        if (data.status === "complete" || data.status === "failed") {
          if (data.status === "complete") {
            trackFunnel("practice_review", "analysis_completed", {
              review_id: data.id,
              demonstrated_count: (data.demonstrated || []).length,
              missed_count: (data.missed || []).length,
              focus_count: (data.next_focus || []).length,
            });
          }
          await loadHistory();
        }
      } catch { /* keep polling */ }
    }, 2500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [active, loadHistory]);

  // P0 (Feb 8, 2026) — iPhone preparation-stage failure surface.
  // When the picker hands us a dead/0-byte/unreadable handle, OR
  // the file disappears between picker close and chunked-init, we
  // record the reason here so the upload zone NEVER silently resets.
  // Cleared on a fresh successful pick or on user dismissal.
  const [prepareFailure, setPrepareFailure] = useState(null);

  const onFiles = async (filesList) => {
    // ── P0 instant feedback — render the panel before we do ANY
    // async work so iOS Safari can't appear to "do nothing".
    setPrepareFailure(null);
    setUploadPhase("preparing");

    const file = filesList?.[0];
    if (file) {
      // P0 May 20, 2026 — universal picker diagnostic anchor. Emits
      // `handoff_files_staged` so the visible picker debug panel
      // shows the chain reaching the UI staged state.
      picker.markFilesStaged(1);
    }
    if (!file) {
      logUploadEvent("picker.no_file_returned", {
        list_length: (filesList && filesList.length) || 0,
        is_iphone: isIphoneLikely(),
      });
      setUploadPhase("idle");
      setPrepareFailure({
        reason: "no_file_returned",
        title: "No video came through from the picker",
        message: isIphoneLikely()
          ? "iPhone Photos sometimes hands the page nothing — usually after an iCloud download was cancelled or timed out. Try again, or pick from Files > On My iPhone."
          : "The picker closed without returning a file. Tap Choose file and try again.",
        suggestFilesPicker: isIphoneLikely(),
      });
      truth.setState("failed", "No file came through — please try again.", {
        id: "lr-picker-empty", duration: 8000,
      });
      return;
    }

    logUploadEvent("picker.file_selected", {
      name: file.name,
      size: file.size,
      type: file.type,
      last_modified: file.lastModified,
      from_photos_picker: isLikelyFromPhotosPicker(file),
      is_iphone: isIphoneLikely(),
    });

    if (file.size > MAX_BYTES) {
      logUploadEvent("picker.too_large", { size: file.size, cap: MAX_BYTES });
      setUploadPhase("idle");
      truth.setState("failed", `File too large — max ${MAX_BYTES / (1024 * 1024 * 1024)} GB.`);
      return;
    }

    // P0 (Feb 8, 2026) — large-file advisory for iPhone/iPad uploads.
    // Production reports show ~2.4 GB cellular uploads failing
    // silently. Set expectation BEFORE the upload starts so the user
    // knows to stay on Wi-Fi + keep the screen on. Non-blocking —
    // just primes them for a long upload.
    const SIZE_ADVISORY_BYTES = 1.5 * 1024 * 1024 * 1024; // 1.5 GB
    if (file.size >= SIZE_ADVISORY_BYTES && isIphoneLikely()) {
      logUploadEvent("picker.large_file_iphone_advisory_shown", {
        size_mb: Math.round(file.size / (1024 * 1024)),
      });
      truth.setState("waiting", 
        `Large video detected (${Math.round(file.size / (1024 * 1024 * 1024) * 10) / 10} GB). Upload may take longer depending on your connection.`,
        { id: "lr-iphone-large-advisory", duration: 8000 },
      );
    }

    // ── P0 Feb 8, 2026 — duplicate-pick detector.
    // When the user re-picks the same video twice (live iPad/iPhone
    // pattern: first attempt reached 100%, screen flipped away,
    // user re-picks the same file fresh from Photos), the existing
    // review is still processing server-side. Picking again would
    // start a duplicate session + duplicate review row — wasted
    // bandwidth + a confused inbox.
    //
    // We check the recovery pointer for this exact file. Hit →
    // surface the banner immediately, log the event, skip runUpload.
    // The user's Continue tap takes them to the existing review;
    // their Dismiss tap clears the pointer and lets a fresh upload
    // proceed (their explicit choice).
    if (user) {
      const dup = findRecoveryByFile(
        user._id || user.id || user.email,
        file,
        (user.role || "").toLowerCase() || null,
        "self_review",
      );
      if (dup) {
        logUploadEvent("picker.duplicate_pick_detected", {
          review_id: dup.reviewId,
          status: dup.status,
          age_seconds: Math.round((Date.now() - dup.ts) / 1000),
          file_size_mb: Math.round(file.size / (1024 * 1024)),
        });
        try {
          // Try the localStorage rehydrate path first — gives the
          // user the FULL active review surface, not just a banner.
          const { data } = await axios.get(
            `${API}/api/lesson-review/${dup.reviewId}`,
            { withCredentials: true },
          );
          if (data && data.id && !isTerminalStatus(data.status)) {
            setActive(data);
            setUploadPhase("idle");
            truth.setState("waiting", 
              "We found your previous upload — it's still processing. Picking up where you left off.",
              { id: "lr-duplicate-pick", duration: 7000 },
            );
            return;
          }
          // Stale pointer (404 / terminal) — clear and proceed.
          clearRecentReview(user._id || user.id || user.email);
        } catch {
          // Fetch failed — fall back to the banner via recoveryReview.
          setRecoveryReview({
            id: dup.reviewId,
            status: dup.status || "processing",
            filename: dup.fileName,
            size_bytes: dup.fileSize,
          });
          setUploadPhase("idle");
          truth.setState("waiting", 
            "We found your previous upload — tap Continue to pick it up.",
            { id: "lr-duplicate-pick", duration: 7000 },
          );
          return;
        }
      }
    }

    // ── P0 Feb 8, 2026 — readability pre-flight.
    // Guards against:
    //   • zero-byte File handle (iCloud download never finished)
    //   • dead File handle (iOS released the temp export before our
    //     JS got hold of the bytes)
    //   • probe timeout (page was backgrounded mid-pick — Photos
    //     handoff still in flight, OS hasn't released the bytes)
    //
    // Any of these used to silently return on `if (!file) return` or
    // crash the chunked-upload init — the user saw the upload-zone
    // re-render with nothing to show for it. NOW the user gets an
    // explicit recovery banner with re-try guidance.
    const readable = await probeFileReadability(file);
    if (!readable.ok) {
      logUploadEvent("picker.unreadable_file", {
        reason: readable.reason,
        detail: readable.detail || null,
        size: file.size,
        is_iphone: isIphoneLikely(),
        from_photos_picker: isLikelyFromPhotosPicker(file),
      });
      setUploadPhase("idle");
      setPrepareFailure({
        reason: readable.reason,
        title: readable.reason === "empty_file"
          ? "That video is empty"
          : readable.reason === "timeout"
          ? "iCloud is still preparing this video"
          : "We couldn't read this video",
        message:
          readable.reason === "empty_file"
            ? (isIphoneLikely()
              ? "iPhone returned a 0-byte file — usually means the iCloud download didn't finish. Tap the video in Photos to pre-download it, or pick from Files > On My iPhone."
              : "The picker returned a 0-byte file. Try again with the original recording.")
            : readable.reason === "timeout"
            ? "Your iPhone is still fetching this video from iCloud. Wait until the Photos thumbnail shows fully, then try again — or pick from Files > On My iPhone for the most reliable result."
            : "The file handle was lost before the upload could start. Re-pick the same video to try again.",
        suggestFilesPicker: shouldSuggestFilesPicker(file) || readable.reason === "timeout",
        fileName: file.name,
      });
      truth.setState("failed", 
        readable.reason === "empty_file"
          ? "Empty file — iCloud may not have finished downloading"
          : readable.reason === "timeout"
          ? "iCloud is still preparing this video — try again or pick from Files"
          : "Couldn't read the file — please re-pick",
        { id: "lr-picker-unreadable", duration: 9000 },
      );
      return;
    }

    // ── P0 Feb 8, 2026 — iPhone-specific advisory.
    // Files > 500 MB picked from Photos on iPhone fail more often
    // than the same file picked via Files > On My iPhone (which
    // bypasses the iCloud round-trip). Show a non-blocking tip and
    // log it for telemetry.
    if (shouldSuggestFilesPicker(file)) {
      logUploadEvent("picker.iphone_files_hint_shown", {
        size_mb: Math.round(file.size / (1024 * 1024)),
      });
      truth.setState("waiting", 
        "Tip — for videos this large on iPhone, picking from Files > On My iPhone is more reliable than Photos.",
        { id: "lr-iphone-files-hint", duration: 7000 },
      );
    }

    // P0 (Feb 7 2026) background-safe — if we have a pending record
    // from a prior session, try to resume under that upload_id when
    // the file identity matches. Zero bytes lost, zero re-upload.
    //
    // iPhone-recovery hardening (Feb 8, 2026): structured comparator
    // probes the picked video's duration too. This catches the case
    // where two adjacent iPhone recordings have IDENTICAL filenames
    // and very similar sizes (iCloud variants of the same recording)
    // but differ by a few seconds of duration. The user gets a
    // SPECIFIC explanation of why their pick was rejected so they
    // can pick the correct file confidently.
    if (pendingRecord) {
      const cmp = await compareFileToPendingRecordWithDuration(file, pendingRecord);
      if (cmp.matches) {
        const recoveredId = pendingRecord.uploadId;
        setPendingRecord(null);
        setPausedUpload(null);
        setUploadFile(file);
        trackFunnel("practice_review", "upload_background_resumed", {
          size_mb: Math.round(file.size / 1024 / 1024),
        });
        await runUpload(file, recoveredId);
        return;
      }
      // Mismatch — tell the user EXACTLY why so they can pick the
      // right file. iPhone thumbnails are nearly identical, so a
      // bare "wrong file" error doesn't help. Itemise the diff.
      const reason = describeMismatch(cmp.mismatches, pendingRecord)
        || "This doesn't look like the same video. Please choose the original video to continue.";
      setUploadPhase("idle");
      truth.setState("failed", reason, {
        duration: 7000,
        id: "lr-upload-mismatch",
      });
      return;
    }
    // ── P0 Feb 9, 2026 — gate fresh uploads on the human-readable
    // pre-upload form so the resulting review row has a real Title
    // (and optional Camera label + Notes) instead of a cryptic camera
    // filename like "NO20260503-164104-000572.mov". The form is
    // mounted via `pendingDetailsFile` below the upload zone; the
    // Continue button calls `startFreshUploadWithDetails(file,
    // details)` which runs the original probe → runUpload chain.
    //
    // New pick discards any previously paused upload + previous
    // form details (re-derived from this fresh pick).
    setPausedUpload(null);
    setUploadFile(file);
    setUploadDetails(null);
    setPendingDetailsFile(file);
    setUploadPhase("idle");
  };

  // Fresh-upload starter — invoked by the PreUploadDetailsForm's
  // Continue button. Mirrors the previous tail of `onFiles` (duration
  // probe + top-level guard around runUpload).
  const startFreshUploadWithDetails = async (file, details) => {
    if (!file) return;
    setPendingDetailsFile(null);
    setUploadDetails(details || null);
    pickedDurationRef.current = null;
    logUploadEvent("picker.duration_probe_started", { name: file.name });
    probeVideoDurationSeconds(file).then((seconds) => {
      logUploadEvent("picker.duration_probe_done", {
        name: file.name,
        seconds,
      });
      if (typeof seconds === "number") {
        pickedDurationRef.current = seconds;
        if (user) {
          const existing = loadPendingUpload(
            user._id || user.id || user.email, "lesson_review",
          );
          if (existing?.uploadId) {
            savePendingUpload({
              userId: existing.userId,
              purpose: existing.purpose,
              uploadId: existing.uploadId,
              videoDurationSeconds: seconds,
              fileType: file.type,
            });
          }
        }
      }
    });
    try {
      logUploadEvent("upload.started", {
        name: file.name,
        size: file.size,
        is_iphone: isIphoneLikely(),
        has_display_title: !!(details && details.display_title),
        has_camera_label: !!(details && details.camera_label),
        has_notes: !!(details && details.notes),
      });
      await runUpload(file, null, details || null);
    } catch (err) {
      logUploadEvent("upload.interrupted", {
        stage: "pre_chunked_init",
        message: err && err.message ? String(err.message).slice(0, 200) : "",
      });
      setUploadPhase("idle");
      setPrepareFailure({
        reason: "preparation_threw",
        title: "Upload couldn't start",
        message: "Something interrupted the handoff between the picker and the uploader. Please try again — your file is still on the device.",
        suggestFilesPicker: isIphoneLikely(),
        fileName: file.name,
      });
      truth.setState("failed", 
        err?.message || "Upload couldn't start — please try again.",
        { id: "lr-upload-prep-failed", duration: 9000 },
      );
    }
  };

  // Cancel handler for the pre-upload details form — discard the
  // picked file so the user can re-pick or change their mind.
  const cancelPendingDetails = () => {
    logUploadEvent("upload.pre_upload_form_cancelled", {
      file_name: pendingDetailsFile?.name || null,
    });
    setPendingDetailsFile(null);
    setUploadDetails(null);
    setUploadFile(null);
    setUploadPhase("idle");
  };

  // Sleep helper — used to enforce the spec'd minimum dwell on the
  // "Optimising" and "Finalising" stages. Even if the real work is
  // instant, the user needs a beat to register the change.
  const _wait = (ms) => new Promise((r) => setTimeout(r, ms));

  // Called both for first-time uploads (resumeUploadId=null) and for
  // resuming a paused upload. Centralises retry/pause/auth handling
  // so the UX is identical regardless of entry point.
  //
  // `details` (Feb 9, 2026) — pre-upload human-readable identifiers
  // captured by the PreUploadDetailsForm. Defaults to `uploadDetails`
  // state so resume callers (paused-resume button, background-safe
  // recovery) don't have to plumb the values manually.
  const runUpload = async (file, resumeUploadId, details = uploadDetails) => {
    if (!file) return;
    trackFunnel(
      "practice_review",
      resumeUploadId ? "upload_resumed" : "upload_started",
      { size_mb: Math.round(file.size / 1024 / 1024), kind: file.type },
    );
    setUploading(true);
    setUploadFile(file);

    // P0 (Feb 7 2026) — RESUME PATH.
    // On resume we MUST NOT transition through "preparing" (that
    // forces the panel to display 0%) and we MUST seed uploadPct
    // from server truth BEFORE any chunk is uploaded. This kills
    // the "top bar resets to 0 then climbs to 42%" UX bug.
    //
    // Source of truth precedence (highest first):
    //   1. pausedUpload state (most recent local pause snapshot)
    //   2. pendingRecord from /status (background-safe recovery)
    //   3. fall back to current uploadPct (no seed available)
    if (resumeUploadId) {
      let seededPct = uploadPct;
      let seededFromChunks = -1;
      let seededTotal = 0;
      if (pausedUpload?.totalChunks) {
        seededFromChunks = Math.max(0, (pausedUpload.lastSuccessful ?? -1) + 1);
        seededTotal = pausedUpload.totalChunks;
      } else if (pendingRecord?.serverTotal) {
        seededFromChunks = pendingRecord.serverReceived || 0;
        seededTotal = pendingRecord.serverTotal;
      }
      if (seededTotal > 0) {
        seededPct = Math.round((seededFromChunks / seededTotal) * 100);
      }
      setUploadPct(seededPct);
      if (typeof console !== "undefined" && console.info) {
        console.info(
          `[LessonReview] resume seed: uploadId=${resumeUploadId} ` +
          `received=${seededFromChunks}/${seededTotal} ` +
          `nextChunkIndex=${seededFromChunks} ` +
          `displayedPercent=${seededPct}%`,
        );
      }
      // Skip "preparing" entirely — go straight to uploading with
      // the seeded pct already in place.
      setUploadPhase("uploading");
    } else {
      setUploadPct(0);
      // ── Spec §1: instant feedback, < 300ms ─────────────────────
      setUploadPhase("preparing");
    }

    // ── Spec §2: optimising stage. On a fresh upload (not a resume),
    // attempt client-side compression if the feature flag is on AND
    // the file passes shouldCompress() gates. Any failure falls back
    // silently to uploading the original — the chunked-upload pipeline
    // is never compromised.
    let workingFile = file;
    if (!resumeUploadId) {
      await _wait(900);
      const decision = shouldCompress(file);
      if (decision.compress) {
        setUploadPhase("optimising");
        setOptimisePct(0);
        try {
          trackFunnel("practice_review", "compression_started", {
            size_mb: Math.round(file.size / 1024 / 1024),
          });
          const compressed = await compressVideo(file, {
            onProgress: ({ pct }) => setOptimisePct(pct),
          });
          if (compressed && compressed.size < file.size) {
            workingFile = compressed;
            setUploadFile(compressed);
            trackFunnel("practice_review", "compression_completed", {
              orig_mb: Math.round(file.size / 1024 / 1024),
              new_mb: Math.round(compressed.size / 1024 / 1024),
              saved_pct: Math.round(
                ((file.size - compressed.size) / file.size) * 100,
              ),
            });
          }
        } catch (err) {
          // Fallback path — log + keep going with the original file.
          // Owner directive: "Do not crash. Do not block the user."
          if (typeof console !== "undefined" && console.warn) {
            console.warn(
              `[LessonReview] compression skipped (${err?.code || "error"}): ${err?.message || err}. Uploading original.`,
            );
          }
          trackFunnel("practice_review", "compression_skipped", {
            reason: err?.code || "error",
          });
          workingFile = file;
          setUploadFile(file);
        }
      } else if (
        decision.reason !== "feature-off" && decision.reason !== "no-file"
      ) {
        // Briefly show the optimising stage even when we don't compress
        // — keeps the UX consistent. (Spec §2: 1-2s minimum dwell.)
        setUploadPhase("optimising");
        await _wait(800);
      }
    }
    setUploadPhase("uploading");

    // ── P0 Feb 8, 2026 — log which upload path is actually live so
    // we can confirm Stage 1 (chunked, FastAPI relay) vs Stage 2
    // (direct-to-R2) from the browser console without redeploying.
    //
    // Feb 8, 2026 (Stage 2 phase-1 rollout): the routing decision now
    // takes file size + role into account when env is "auto". The
    // adapter logs `upload.routing_decision` separately; this log
    // is the LessonReview-side mirror that the panel picks up for
    // the user-facing "Direct cloud upload active" badge.
    const _routingCtx = {
      file: workingFile,
      role: (user?.role || "").toLowerCase(),
    };
    const cloudPath = isCloudUploadEnabled(_routingCtx);
    logUploadEvent("upload.path_selected", {
      stage: cloudPath ? "stage_2_cloud_r2" : "stage_1_chunked_relay",
      complete_url: cloudPath
        ? "/api/lesson-review/cloud-complete"
        : "/api/lesson-review/chunked-complete",
      file_size_mb: Math.round(workingFile.size / (1024 * 1024)),
      is_iphone: isIphoneLikely(),
      role: _routingCtx.role,
    });
    setActiveUploadStage(cloudPath ? "stage_2_cloud_r2" : "stage_1_chunked_relay");

    const _startedAt = Date.now();
    let _uploadFinishedAt = null;
    try {
      // Feb 6, 2026: chunked upload (5 MiB pieces) replaces direct
      // multipart POST so 2 GB / 20+ minute lesson recordings survive
      // the proxy without a 502. Backend reassembles into the same
      // canonical lesson_review path and runs the same downstream
      // pipeline — AI analysis flow is unchanged.
      // Feb 7, 2026 (speed bump): chunks are now 25 MiB, 3 in
      // parallel, with throttled progress updates. Server controls
      // chunk size via /init response so this is a one-line change.
      // Feb 8, 2026 (Stage 2 phase-1 rollout): direct-to-R2 multipart
      // is now auto-selected for instructor-side ≥ 500 MB uploads.
      // Stage 1 stays as the proven fallback for everything else
      // and as the auto-fallback when /cloud/init fails.
      const data = await adaptiveUpload({
        file: workingFile,
        purpose: "lesson_review",
        context: {},
        userRole: _routingCtx.role,
        completeUrl: cloudPath
          ? `/api/lesson-review/cloud-complete`
          : `/api/lesson-review/chunked-complete`,
        completeBody: {
          ...(aiReviewTier ? { ai_review_tier: aiReviewTier } : {}),
          ...(details && details.display_title
            ? { display_title: details.display_title } : {}),
          ...(details && details.notes
            ? { notes: details.notes } : {}),
          ...(details && details.camera_label
            ? { camera_label: details.camera_label } : {}),
        },
        onProgress: ({
          pct, stage, upload_id, last_successful_chunk, total_chunks, mbps,
          retry_count, last_http_status, last_error_detail, last_failed_chunk,
          parallelism, session_age_seconds, current_chunk,
        }) => {
          setUploadPct(pct);
          if (typeof mbps === "number") setUploadMbps(mbps);
          // Feb 8, 2026 — diagnostic snapshot for the in-app debug
          // panel. Always updated; the panel is only rendered when
          // localStorage.lr_debug_uploads === "true".
          setUploadDebug({
            upload_id: upload_id || null,
            stage,
            current_chunk: current_chunk ?? null,
            last_successful_chunk: last_successful_chunk ?? null,
            total_chunks: total_chunks ?? null,
            last_failed_chunk: last_failed_chunk ?? null,
            last_http_status: last_http_status ?? null,
            last_error_detail: last_error_detail ?? null,
            retry_count: retry_count ?? 0,
            parallelism: parallelism ?? null,
            session_age_seconds: session_age_seconds ?? null,
            mbps: typeof mbps === "number" ? mbps : null,
          });
          // Spec §5: bridge cleanly into the held "finalising" stage
          // when the helper signals the assemble call is happening.
          if (stage === "finalizing" && uploadPhase !== "finalising") {
            setUploadPhase("finalising");
            // Capture the moment upload bytes finished flying so we
            // can log finalize duration separately.
            if (!_uploadFinishedAt) _uploadFinishedAt = Date.now();
          }
          // P0 background-safe persistence — snapshot every meaningful
          // tick so iOS tab eviction can't lose the upload pointer.
          if (upload_id && user) {
            const uid = user._id || user.id || user.email;
            savePendingUpload({
              userId: uid,
              purpose: "lesson_review",
              uploadId: upload_id,
              fileName: workingFile.name,
              fileSize: workingFile.size,
              fileLastModified: workingFile.lastModified,
              fileType: workingFile.type,
              // Feb 8, 2026 iPhone-recovery — duration + last-pct
              // travel into the persisted record so the recovery
              // banner can guide the user back to the EXACT video.
              videoDurationSeconds: (
                typeof pickedDurationRef.current === "number"
                  ? pickedDurationRef.current
                  : undefined
              ),
              lastProgressPct: (typeof pct === "number" ? pct : undefined),
              totalChunks: total_chunks,
              lastSuccessfulChunk: last_successful_chunk,
              reason: stage === "paused" ? "paused-network" : "uploading",
            });
          }
        },
        resumeUploadId: resumeUploadId || undefined,
      });
      setActive(data.review);
      // ── P0 (Feb 8, 2026) — sticky pointer to the just-uploaded
      // review so a page reload / iOS background eviction during
      // the 2.7s "Finalising → Complete" animation cannot dump
      // the user back to an empty upload zone with no way to find
      // their processing review. Cleared when the review reaches
      // a terminal state or the user explicitly dismisses.
      //
      // Stores filename + size so a duplicate-pick (user re-picks
      // the same video twice) is detected via `findRecoveryByFile`
      // and surfaces the recovery banner instead of starting a
      // fresh upload session.
      if (user && data.review?.id) {
        rememberRecentReview({
          userId: user._id || user.id || user.email,
          reviewId: data.review.id,
          status: data.review.status,
          fileName: workingFile?.name || null,
          fileSize: workingFile?.size || null,
          role: (user.role || "").toLowerCase() || null,
          reviewType: "self_review",
        });
        logUploadEvent("recovery.pointer_written", {
          review_id: data.review.id,
          status: data.review.status,
          role: (user.role || "").toLowerCase() || null,
          review_type: "self_review",
          file_size_mb: workingFile?.size
            ? Math.round(workingFile.size / (1024 * 1024)) : null,
        });
      }
      logUploadEvent("upload.complete_response", {
        review_id: data.review?.id || null,
        status: data.review?.status || null,
        idempotent: !!data.idempotent,
      });
      setPricingQuote(data.quote || null);
      if (data.review?.status === "awaiting_payment" && !data.review.paid && data.quote) {
        setPricingOpen(true);
      }
      trackFunnel("practice_review", "upload_completed", { review_id: data.review?.id });
      // Owner spec — log full speed diagnostics for triage.
      if (typeof console !== "undefined" && console.info) {
        const finishedAt = Date.now();
        const uploadMs = (_uploadFinishedAt || finishedAt) - _startedAt;
        const finalizeMs = finishedAt - (_uploadFinishedAt || finishedAt);
        const sizeMb = workingFile.size / (1024 * 1024);
        const avgMbps = uploadMs > 0 ? (workingFile.size * 8) / 1_000_000 / (uploadMs / 1000) : null;
        console.info(
          `[LessonReview] upload speed report: uploadId=${data.review?.id || "?"} ` +
          `fileSize=${sizeMb.toFixed(1)}MB chunkSize=server-defined ` +
          `mbps_avg=${avgMbps?.toFixed(2) || "n/a"} ` +
          `time_uploading_ms=${uploadMs} time_finalizing_ms=${finalizeMs} ` +
          `userAgent="${navigator?.userAgent || ""}"`,
        );
      }
      setPausedUpload(null);
      setPendingRecord(null);
      setUploadMbps(null);
      if (user) clearPendingUpload(user._id || user.id || user.email, "lesson_review");
      // May 14, 2026 (operator audit — diagnostics): emit canonical
      // marker for the upload → AI review handoff so future regression
      // hunts can grep production logs.
      // eslint-disable-next-line no-console
      console.info("[upload-continuity] upload_finalize_success_handoff_to_ai", {
        review_id: data.review?.id || null,
        upload_id: data.upload_id || null,
        local_purge: true,
      });
      // ── Spec §5: hold "Finalising" for >= 1.5s so the user gets
      // emotional closure before "complete" flashes by.
      //
      // Feb 8, 2026 P0 — every line BELOW here runs AFTER the review
      // doc is created server-side. Wrap in try/finally so an
      // unrelated throw (toast lib, _wait race, iOS Safari freeze
      // during background) can never unmount the active review
      // surface. The `active` state is already set; the finalising
      // animation is purely cosmetic.
      try {
        setUploadPhase("finalising");
        await _wait(1500);
        setUploadPhase("complete");
        await _wait(1200);
        setUploadPhase("idle");
        setUploadFile(null);
        setActiveUploadStage(null);
        setUploadDetails(null);
        truth.setState("completed", "Uploaded — choose a review option");
      } catch (animErr) {
        logUploadEvent("upload.finalize_animation_threw", {
          message: animErr && animErr.message ? String(animErr.message).slice(0, 200) : "",
        });
        // The review row exists and `active` is set — quietly drop
        // back to idle without resetting `active`.
        setUploadPhase("idle");
        setUploadFile(null);
        setActiveUploadStage(null);
      }
    } catch (e) {
      if (e instanceof AuthExpiredError) {
        // P0 — keep the user's progress on screen so they can resume
        // after re-login. The server-side upload session is still
        // intact (`received_chunks` survives), so calling chunkedUpload
        // again with the same file picks up where we left off.
        setPausedUpload({
          uploadId: e.uploadId,
          file: workingFile,
          lastSuccessful: e.currentChunk - 1,
          totalChunks: e.totalChunks,
          reason: "auth",
        });
        if (user) {
          savePendingUpload({
            userId: user._id || user.id || user.email,
            purpose: "lesson_review",
            uploadId: e.uploadId,
            fileName: workingFile.name,
            fileSize: workingFile.size,
            fileLastModified: workingFile.lastModified,
            totalChunks: e.totalChunks,
            lastSuccessfulChunk: e.currentChunk - 1,
            reason: "paused-auth",
          });
        }
        setUploadPhase("paused");
        truth.setState("failed", 
          "Your session expired during upload. Sign in again — your progress is saved.",
          {
            duration: Infinity,
            id: "lr-upload-auth-expired",
            action: {
              label: "Sign in",
              onClick: () => {
                const here = window.location.pathname + window.location.search;
                window.location.assign(`/login?returnTo=${encodeURIComponent(here)}`);
              },
            },
          },
        );
      } else if (e instanceof UploadPausedError) {
        // P0 (Feb 7 2026) — chunk retries exhausted. Pause, never die.
        // Server-side session lives 24h; user can tap Resume — and the
        // UploadProgressPanel auto-retries after 25s if connectivity
        // returned (spec §4: "auto retry after ~20-30s").
        //
        // Feb 8, 2026 — propagate the deterministic failure context
        // (failed chunk index, HTTP status, server detail) into the
        // pausedUpload snapshot and the in-app debug panel so the user
        // can read it straight off their phone instead of digging in
        // dev tools.
        setPausedUpload({
          uploadId: e.uploadId,
          file: workingFile,
          lastSuccessful: e.lastSuccessfulChunk,
          totalChunks: e.totalChunks,
          reason: "network",
          failedChunkIndex: e.failedChunkIndex ?? null,
          lastHttpStatus: e.lastHttpStatus ?? null,
          lastErrorDetail: e.lastErrorDetail ?? null,
          totalAttempts: e.totalAttempts ?? null,
        });
        if (user) {
          savePendingUpload({
            userId: user._id || user.id || user.email,
            purpose: "lesson_review",
            uploadId: e.uploadId,
            fileName: workingFile.name,
            fileSize: workingFile.size,
            fileLastModified: workingFile.lastModified,
            totalChunks: e.totalChunks,
            lastSuccessfulChunk: e.lastSuccessfulChunk,
            reason: "paused-network",
          });
        }
        setUploadPhase("paused");
      } else {
        // P0 (Feb 8, 2026 — production iPad/iPhone 2.4 GB failure
        // diagnostic). Surface the FULL failure context so the next
        // production report gives us the cause from the browser
        // console without any redeploy required. Replaces the old
        // silent `truth.setState("failed", "Upload failed")`.
        const status = e?.response?.status;
        const detail =
          (typeof e?.response?.data?.detail === "string" && e.response.data.detail)
          || (e?.response?.data?.detail?.message)
          || e?.message
          || "";
        logUploadEvent("upload.failed", {
          stage: "uncaught_error",
          name: e?.name || "",
          message: e?.message ? String(e.message).slice(0, 200) : "",
          http_status: status ?? null,
          detail: typeof detail === "string" ? detail.slice(0, 240) : null,
          file_size_mb: workingFile
            ? Math.round(workingFile.size / (1024 * 1024))
            : null,
          is_iphone: isIphoneLikely(),
        });
        logUploadEvent("upload.reset_reason", {
          stage: "uncaught_error",
          message: e && e.message ? String(e.message).slice(0, 200) : "",
          name: e && e.name ? e.name : "",
        });
        setUploadPhase("error");
        setActiveUploadStage(null);
        // Friendly message — show the server detail when available
        // ("Finalization failed: ..." or "File appears empty" etc.).
        // Generic "Upload failed" only surfaces when there is truly
        // nothing better. Always include a Retry action.
        const niceMsg =
          (detail && !/^network\s*error$/i.test(detail))
            ? detail
            : "Couldn't finish your upload — your chunks may still be on the server. Tap Retry.";
        truth.setState("failed", niceMsg, {
          id: "lr-upload-failed",
          duration: 9000,
          action: {
            label: "Retry",
            onClick: () => {
              // Re-pick the same file → chunkedUpload sees the
              // server-side session, skips uploaded chunks, retries
              // finalize.
              picker.openPicker();
            },
          },
        });
      }
    } finally {
      setUploading(false);
      // Only reset the file picker on a new upload, not on resume —
      // we keep the same File reference for resume to work.
      if (!resumeUploadId && fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    onFiles(e.dataTransfer.files);
  };

  const openDetail = async (id) => {
    setOpen(id);
    setOpenDoc(null);
    try {
      const { data } = await axios.get(`${API}/api/lesson-review/${id}`, { withCredentials: true });
      setOpenDoc(data);
    } catch { truth.setState("failed", "Couldn't load review"); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review and its source file?")) return;
    try {
      await axios.delete(`${API}/api/lesson-review/${id}`, { withCredentials: true });
      truth.setState("completed", "Deleted");
      if (active?.id === id) setActive(null);
      if (open === id) { setOpen(null); setOpenDoc(null); }
      await loadHistory();
    } catch { truth.setState("failed", "Delete failed"); }
  };

  // Single payment-entry path — used by:
  //   • the inline "Choose an option" button on ActiveReview
  //   • the new "Pay for review" buttons on history rows + drawer
  // Pulls the latest doc into the active slot, fetches a fresh quote,
  // then opens the pricing modal. Works whether the review is in
  // history or already active.
  const startPayment = async (review) => {
    if (!review?.id) return;
    try {
      const { data } = await axios.get(
        `${API}/api/lesson-review/${review.id}`,
        { withCredentials: true }
      );
      const fresh = data || review;
      if (fresh.status !== "awaiting_payment" || fresh.paid) {
        truth.setState("waiting", "This review is already past the payment step.");
        await loadHistory();
        return;
      }
      // Best-effort quote refresh — the modal can still render without it.
      try {
        const q = await axios.get(
          `${API}/api/upload-pricing/quote`,
          { params: { duration_seconds: fresh.duration_seconds || 0 },
            withCredentials: true }
        );
        setPricingQuote(q.data);
      } catch { /* modal will pull its own quote if needed */ }
      setActive(fresh);
      setPricingOpen(true);
      // Close detail drawer if open — modal becomes the focus.
      if (open) { setOpen(null); setOpenDoc(null); }
    } catch (e) {
      truth.setState("failed", e.response?.data?.detail || "Couldn't open payment.");
    }
  };

  return (
    <div
      className="bg-[#050505] text-white font-outfit pb-24"
      data-testid="lesson-review-page"
    >
      <header className="sticky inset-x-0 z-40 backdrop-blur-xl bg-[#050505]/85 border-b border-white/[0.07]" style={{ top: "var(--app-top-offset, 0px)" }}>
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="lr-back" onClick={() => goBack()} className="p-2 -ml-2 rounded-lg hover:bg-white/5">
            <ArrowLeft className="w-4 h-4 text-zinc-400" />
          </button>
          <div className="flex-1">
            <p className="text-sm font-bold text-white">Lesson Footage Review</p>
            <p className="text-[10.5px] text-zinc-500 uppercase tracking-[0.18em]">AI · 17 DVSA competencies</p>
          </div>
          {isAdmin && (
            <button
              data-testid="founder-free-toggle"
              onClick={toggleFounderFree}
              title={founderFree ? "Founder Free Mode is ON (admin-only)" : "Founder Free Mode is OFF — you'll pay normally"}
              className={`text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1.5 rounded-lg border flex items-center gap-1.5 ${
                founderFree
                  ? "bg-amber-500 text-black border-amber-500"
                  : "bg-white/5 text-zinc-300 border-white/10 hover:border-[#D4AF37]/40"
              }`}
            >
              <Crown className="w-3 h-3" /> {founderFree ? "Free ON" : "Real"}
            </button>
          )}
          <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
        </div>
      </header>

      {/* Batch P1 · Recording Truth-State — canonical async receipt.
          Docked immediately under the header so every Recording /
          AI-review action's outcome lands consistently. `waiting`
          preserves honesty around accepted-but-not-finished AI
          processing; `failed` exposes Retry + Dismiss. */}
      <div className="max-w-3xl mx-auto px-4 pt-3">
        <TruthReceipt
          state={truth.state}
          message={truth.message}
          onRetry={() => truth.setState(null)}
          onDismiss={() => truth.setState(null)}
        />
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Admin-only bypass-mode warning (renders nothing otherwise) */}
        <BypassModeBanner user={user} />

        {/* INTRO */}
        <section data-testid="lr-intro" className="rounded-3xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#16120a] via-[#0c0c0c] to-[#0a0a0a] p-5 md:p-7">
          <div
            data-testid="lr-build-marker"
            data-build-id="iteration_157"
            className="mb-3 inline-flex items-center gap-2 rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/40 px-3 py-1 text-[10.5px] font-extrabold uppercase tracking-widest text-emerald-200"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            iteration_157 · resume session banner
          </div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-3 inline-flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> Practice Review
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-semibold leading-tight">
            Upload a lesson.<br />
            <span className="text-gold-gradient">Get coached by the engine.</span>
          </h2>
          <p className="mt-3 text-[13.5px] text-zinc-300 leading-relaxed max-w-xl">
            Drop a video or audio file (up to 6 GB · 60+ min supported). We extract the audio,
            transcribe it, and grade against the 17 DVSA Part-3 competencies — with your
            top three things to work on next.
          </p>
          <CoreJourneyMantra className="mt-4" testid="lr-intro-mantra" />
        </section>

        {/* P0 May 20, 2026 — Operational continuity rail.
            Always visible (when there is at least one in-flight or
            recently-failed review) at the top of /lesson-review,
            regardless of role and regardless of viewport. Fixes the
            "review vanishes after upload" iPad operational failure:
            learners were previously filtered out of the only
            pending-review surface on this page, and ipad has no
            global ActiveOperationPill. Doctrine: "Never ghost the
            user. Uploads/reviews cannot disappear silently." */}
        <PendingAnalysisRail
          user={user}
          testIdPrefix="lr-pending-rail"
          className="mb-5"
          onOpen={(review) => {
            setActive(review);
            try {
              window.history.replaceState({}, "", `${window.location.pathname}?id=${review.id}`);
            } catch (_) { /* noop */ }
          }}
        />

        {/* UPLOAD ZONE */}
        {!active && (
          <>
            {/* Deferred AI review queue (Feb 8, 2026) — full task list.
                Lives above the upload zone so an instructor returning
                to /lesson-review on their laptop sees their queued
                tasks immediately and can tap "Upload footage" to
                resume the right one. Anchor id="pending" so the
                dashboard's compact banner can deep-link to it. */}
            {((user?.role || "").toLowerCase() !== "learner") && (
              <div id="pending" data-testid="lr-pending-section" className="mb-5">
                <PendingReviewQueue
                  variant="full"
                  testIdPrefix="lr-pending-queue"
                />
              </div>
            )}

            {/* P0 (Feb 8, 2026) — post-upload completion recovery.
                When the user reaches 100%, the upload finalises
                server-side and a `lesson_reviews` doc is created
                immediately. iOS Safari can background the page during
                the 2.7s "Finalising → Complete" animation hold; on
                resume `active` is null and the user previously got
                dumped to a blank upload zone. This banner uses the
                history-scan recovery vector to surface their already-
                processing review. (The localStorage vector auto-
                rehydrates `active` directly — banner only shows when
                localStorage was wiped but the row still exists.) */}
            {recoveryReview && !uploading && !active && (
              <div
                data-testid="lr-recent-upload-recovery-banner"
                data-recovery-review-id={recoveryReview.id}
                data-recovery-review-status={recoveryReview.status || ""}
                className="rounded-2xl border border-emerald-400/40 bg-emerald-500/[0.08] p-4 mb-3"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center flex-none">
                    <Sparkles className="w-4 h-4 text-emerald-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-extrabold text-emerald-100">
                      We found your completed upload — continue processing
                    </p>
                    <p className="text-[12px] text-emerald-100/85 mt-1 leading-snug">
                      Your last upload finished but the page reloaded before you saw the report card. Tap below to pick it back up — the AI is still working on it.
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Button
                        data-testid="lr-recent-upload-recovery-resume"
                        onClick={() => {
                          setActive(recoveryReview);
                          setRecoveryReview(null);
                          if (user) {
                            rememberRecentReview({
                              userId: user._id || user.id || user.email,
                              reviewId: recoveryReview.id,
                              status: recoveryReview.status,
                              fileName: recoveryReview.filename || null,
                              fileSize: recoveryReview.size_bytes || null,
                              role: (user.role || "").toLowerCase() || null,
                              reviewType: "self_review",
                            });
                          }
                          logUploadEvent("upload.recovery_banner_resumed", {
                            review_id: recoveryReview.id,
                          });
                        }}
                        className="h-9 px-3 rounded-md bg-emerald-400 hover:bg-emerald-300 text-[#04140a] text-[12px] font-extrabold"
                      >
                        Continue
                      </Button>
                      <button
                        data-testid="lr-recent-upload-recovery-dismiss"
                        onClick={() => setRecoveryReview(null)}
                        className="h-9 px-3 rounded-md text-zinc-300 text-[12px] font-semibold hover:bg-white/[0.05]"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* P0 Feb 9, 2026 — Resume Session banner. Surfaces an
                incomplete clip basket OR an interrupted review that
                survived a logout/login cycle. Server-side data was
                always preserved — this banner is the missing UI.

                E3 (May 14, 2026): ResumeSessionBanner retired —
                Active Operation pill + /operations history cover
                this case. The resumeBasket / resumeReview state
                remains in scope because it still feeds the
                in-page resume-on-pick detection below. */}

            {/* E3 (May 14, 2026): UploadRecoveryBanner retired —
                same reasoning. The pendingRecord state is still
                used by the file-pick handler to detect "you're
                re-picking the same in-flight file" and prevent
                duplicate uploads. */}
          {/* P0 (Feb 8, 2026) iPhone-recovery — preparation-stage failure banner.
              Surfaces when the picker handed us nothing / a 0-byte handle /
              an unreadable handle, OR when the picker→runUpload transition
              threw. Replaces the old silent-reset behaviour that left the
              user staring at the upload zone with no idea what happened. */}
          {prepareFailure && !uploading && (
            <div
              data-testid="lr-prepare-failure-banner"
              data-prepare-failure-reason={prepareFailure.reason}
              className="rounded-2xl border border-rose-400/40 bg-rose-500/[0.07] p-4 mb-3"
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-rose-500/15 border border-rose-400/30 flex items-center justify-center flex-none">
                  <Upload className="w-4 h-4 text-rose-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-extrabold text-rose-100">
                    {prepareFailure.title}
                  </p>
                  <p className="text-[12px] text-rose-100/85 mt-1 leading-snug">
                    {prepareFailure.message}
                  </p>
                  {prepareFailure.fileName && (
                    <p className="text-[11px] text-rose-200/60 mt-1 truncate">
                      File: {prepareFailure.fileName}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button
                      data-testid="lr-prepare-failure-retry"
                      onClick={() => {
                        setPrepareFailure(null);
                        picker.openPicker();
                      }}
                      className="h-9 px-3 rounded-md bg-rose-400 hover:bg-rose-300 text-[#1a0405] text-[12px] font-extrabold"
                    >
                      Try again
                    </Button>
                    {prepareFailure.suggestFilesPicker && (
                      <Button
                        data-testid="lr-prepare-failure-files"
                        onClick={() => {
                          setPrepareFailure(null);
                          // accept="*/*" + capture absent → iOS opens
                          // the Files document picker instead of Photos.
                          const inp = document.createElement("input");
                          inp.type = "file";
                          inp.accept = ACCEPTED;
                          inp.onchange = (ev) => onFiles(ev.target.files);
                          inp.click();
                        }}
                        className="h-9 px-3 rounded-md bg-white/10 hover:bg-white/15 text-zinc-100 text-[12px] font-semibold"
                      >
                        Pick from Files
                      </Button>
                    )}
                    <button
                      data-testid="lr-prepare-failure-dismiss"
                      onClick={() => setPrepareFailure(null)}
                      className="h-9 px-3 rounded-md text-zinc-300 text-[12px] font-semibold hover:bg-white/[0.05]"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {!uploading && uploadPhase === "idle" && !active && (
            <div
              data-testid="lr-mode-tabs"
              className="mb-3 inline-flex items-center gap-1 rounded-xl border border-white/10 bg-white/[0.02] p-1"
            >
              <button
                type="button"
                data-testid="lr-mode-single"
                onClick={() => setLessonReviewMode("single")}
                className={`px-4 py-2 rounded-lg text-[12px] font-extrabold transition ${
                  lessonReviewMode === "single"
                    ? "bg-[#D4AF37] text-[#050505]"
                    : "text-zinc-400 hover:text-zinc-200"
                }`}
              >
                Single clip
              </button>
              <button
                type="button"
                data-testid="lr-mode-multi"
                onClick={() => setLessonReviewMode("multi")}
                className={`px-4 py-2 rounded-lg text-[12px] font-extrabold transition ${
                  lessonReviewMode === "multi"
                    ? "bg-indigo-500 text-white"
                    : "text-zinc-400 hover:text-zinc-200"
                }`}
              >
                Multi-clip lesson
              </button>
            </div>
          )}
          {lessonReviewMode === "multi" && !uploading && uploadPhase === "idle" && !active && (
            <section
              data-testid="lr-multi-clip-zone"
              className="mb-3 rounded-3xl border-2 border-dashed border-indigo-400/30 bg-indigo-500/[0.04] p-7 md:p-12 text-center"
            >
              {useV2Basket && (
                <div
                  data-testid="lr-upload-basket-v2-active-badge"
                  className="inline-flex items-center gap-1.5 mb-4 px-3 py-1 rounded-full bg-amber-400/15 border border-amber-300/50 text-amber-200 text-[10.5px] font-extrabold uppercase tracking-widest"
                >
                  <Sparkles className="w-3 h-3" />
                  Upload Basket V2 Active
                </div>
              )}
              <div className="w-14 h-14 mx-auto rounded-2xl bg-indigo-500/15 border border-indigo-400/40 flex items-center justify-center mb-4">
                <FileVideo className="w-6 h-6 text-indigo-300" />
              </div>
              <div className="font-display text-lg font-semibold">
                Combine multiple clips into one lesson
              </div>
              <div className="text-[12.5px] text-zinc-400 mt-1 max-w-md mx-auto">
                Pick several short dashcam clips (3–5 min each). We stitch them into one lesson and analyse the combined session as a single review.
              </div>
              <Button
                data-testid="lr-multi-clip-start"
                onClick={() => setBasketOpen(true)}
                className="mt-5 h-11 px-6 rounded-md bg-indigo-500 hover:bg-indigo-400 text-white text-[13px] font-bold"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Start a multi-clip lesson
              </Button>
            </section>
          )}
          {pendingDetailsFile && !uploading && uploadPhase === "idle" && !active && (
            <PreUploadDetailsForm
              file={pendingDetailsFile}
              onContinue={(details) =>
                startFreshUploadWithDetails(pendingDetailsFile, details)
              }
              onCancel={cancelPendingDetails}
              busy={uploading}
            />
          )}
          {lessonReviewMode === "single" && !uploading && uploadPhase === "idle" && (
            <div data-testid="lr-tier-picker-wrap" className="mb-3">
              <AiReviewTierPicker
                context="self_review"
                value={aiReviewTier}
                onChange={setAiReviewTier}
                testIdPrefix="lr"
                compact
              />
            </div>
          )}
          {(lessonReviewMode === "single" || uploadPhase !== "idle") && (
          <section
            data-testid="lr-upload-zone"
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`rounded-3xl border-2 border-dashed p-7 md:p-12 text-center transition ${
              dragOver
                ? "border-[#D4AF37] bg-[#D4AF37]/[0.06]"
                : "border-white/15 bg-white/[0.02] hover:border-[#D4AF37]/40"
            }`}
          >
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center mb-4">
              <Upload className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <div className="font-display text-lg font-semibold">Drop a lesson file</div>
            <div className="text-[12.5px] text-zinc-400 mt-1">
              MP4, MOV, MP3, M4A, WAV — up to 6 GB · 60+ min supported
            </div>
            {/* Feb 8, 2026 — phase-1 Stage 2 rollout reassurance.
                Surfaces ONLY while a Stage 2 (direct-to-R2) upload is
                in flight, so the user sees a calm, professional
                confirmation that large files are now first-class. */}
            {activeUploadStage === "stage_2_cloud_r2" && uploadPhase !== "idle" && (
              <div
                data-testid="lr-stage2-active-badge"
                className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 ring-1 ring-emerald-400/30 px-3 py-1 text-[11px] font-semibold text-emerald-200"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Direct cloud upload active — large videos supported
              </div>
            )}
            <input
              ref={picker.inputRef}
              type="file"
              accept={ACCEPTED}
              onChange={picker.wrapOnChange((_e, files) => onFiles(files))}
              className="hidden"
              data-testid="lr-file-input"
            />
            {picker.recoveryHint && (
              <div className="mt-3">
                <PickerRecoveryCallout
                  source="lesson_review_single"
                  onRePick={() => { picker.clearRecovery(); picker.openPicker(); }}
                  onDismiss={picker.clearRecovery}
                  testid="lr-recovery-callout"
                />
              </div>
            )}
            <Button
              data-testid="lr-pick-btn"
              disabled={uploading}
              onClick={() => picker.openPicker()}
              className="mt-5 h-11 px-6 rounded-md bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] hover:from-[#FAF0C0] hover:to-[#E5C04A] text-[#050505] text-[13px] font-bold guild-cta-glow"
            >
              {uploading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading {uploadPct}%</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Choose file</>
              )}
            </Button>
            {/* iPhone iCloud handoff advisory (May 14, 2026 — production
                bug report: users stuck inside Photos watching a spinner
                before the File handoff). Set expectation BEFORE the
                user taps Choose file so the wait-inside-Photos pattern
                doesn't feel broken. Non-blocking, calm, iPhone-only. */}
            {!uploading && isIphoneLikely() && (
              <p
                data-testid="lr-iphone-icloud-advisory"
                className="mt-3 text-[11px] leading-snug text-white/55 max-w-xs text-center"
              >
                If your video is in iCloud, your phone may prepare it
                before returning to the app. Keep Photos open until it
                comes back.
              </p>
            )}
            {/* Pre-upload screen-awake advisory (May 14, 2026 —
                upload continuity fix). Calm one-liner that sets
                expectation BEFORE the upload starts so users on
                phones know to keep the page open. Always rendered
                pre-upload (not iPhone-gated) because the contract
                applies on every mobile browser. Suppressed during
                an active upload — the in-flight progress panel
                already conveys "this is live, keep it open". */}
            {!uploading && (
              <p
                data-testid="lr-keep-page-open-advisory"
                className="mt-2 text-[11px] leading-snug text-white/55 max-w-xs text-center"
              >
                Keep this page open until upload finishes.
              </p>
            )}
            {/* Truthful wake-lock fallback (May 14, 2026 — upload
                continuity fix). When the browser doesn't support
                the Wake Lock API (Safari &lt; 16.4, in-app
                browsers, some Android Firefox builds), `useWakeLock`
                returns a `fallbackWarning` string. Render it during
                the actual upload so the user understands the
                screen may sleep — never lie about wake-lock
                behaviour. */}
            {uploading && wakeLockFallback && (
              <p
                data-testid="lr-wakelock-fallback"
                className="mt-2 text-[11px] leading-snug text-amber-300/80 max-w-xs text-center"
              >
                {wakeLockFallback}
              </p>
            )}
            {uploadPhase !== "idle" && (
              <UploadProgressPanel
                phase={uploadPhase}
                pct={uploadPhase === "optimising" ? optimisePct : uploadPct}
                fileName={uploadFile?.name || pausedUpload?.file?.name || ""}
                fileSize={uploadFile?.size || pausedUpload?.file?.size || 0}
                mbps={uploadMbps}
                pausedReason={pausedUpload?.reason || null}
                pausedSafe={
                  pausedUpload
                    ? {
                        received: Math.max(0, (pausedUpload.lastSuccessful ?? -1) + 1),
                        total: pausedUpload.totalChunks,
                      }
                    : null
                }
                onResume={() => {
                  // ── Hotfix Feb 8, 2026 ────────────────────────────
                  // The "Try again" / "Start again" button MUST do
                  // something visible. Silent no-op was the live bug.
                  // Order of attempts (logged for production triage):
                  //   1. Auth-paused → redirect to /login.
                  //   2. Network-paused with file+uploadId → resume.
                  //   3. Have original File but no resume id → retry
                  //      from scratch via runUpload(file, null).
                  //   4. Otherwise → reopen file picker + toast.
                  // Every branch logs to console with `[upload-retry]`
                  // so a future silent-failure can never recur.
                  const log = (event, extra = {}) => {
                    if (typeof console !== "undefined" && console.info) {
                      console.info(`[upload-retry] ${event}`, {
                        phase: uploadPhase,
                        has_paused: !!pausedUpload,
                        paused_reason: pausedUpload?.reason || null,
                        paused_upload_id: pausedUpload?.uploadId || null,
                        has_upload_file: !!uploadFile,
                        upload_file_name: uploadFile?.name || null,
                        ...extra,
                      });
                    }
                  };
                  try {
                    log("clicked");
                    if (pausedUpload?.reason === "auth") {
                      log("branch=auth_redirect");
                      const here = window.location.pathname + window.location.search;
                      window.location.assign(`/login?returnTo=${encodeURIComponent(here)}`);
                      return;
                    }
                    if (pausedUpload?.file && pausedUpload?.uploadId) {
                      log("branch=resume", {
                        upload_id: pausedUpload.uploadId,
                        last_successful: pausedUpload.lastSuccessful,
                      });
                      runUpload(pausedUpload.file, pausedUpload.uploadId);
                      return;
                    }
                    // No resumable session — but we still have the
                    // original File object the user picked. Retry
                    // from scratch (new upload_id, full upload).
                    if (uploadFile) {
                      log("branch=retry_from_scratch", {
                        file_size: uploadFile.size,
                      });
                      // Clear stale debug state so the panel snaps
                      // cleanly into "preparing" → "uploading".
                      setUploadDebug(null);
                      setUploadMbps(null);
                      runUpload(uploadFile, null);
                      return;
                    }
                    // True last resort — file reference also lost.
                    // Reopen the file picker; the user must re-pick
                    // the same file (or a different one).
                    log("branch=reopen_picker");
                    setUploadPhase("idle");
                    setUploadDebug(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                    picker.openPicker();
                    truth.setState("needs_input", "Pick the file again to start a fresh upload.",
                      { duration: 4000, id: "lr-upload-retry-pick" },
                    );
                  } catch (err) {
                    log("handler_threw", { error: err?.message });
                    truth.setState("failed", 
                      err?.message || "Couldn't restart the upload — please try picking the file again.",
                      { duration: 5000, id: "lr-upload-retry-error" },
                    );
                  }
                }}
                canResume={
                  // Hotfix Feb 8, 2026 — drives the button label so
                  // a click can never silently no-op. "Try again" is
                  // shown when we genuinely can resume; "Start again"
                  // when the click will reopen / restart from scratch.
                  Boolean(pausedUpload?.file && pausedUpload?.uploadId)
                }
                onCancel={() => {
                  setPausedUpload(null);
                  setPendingRecord(null);
                  setUploadPhase("idle");
                  setUploadFile(null);
                  setUploadMbps(null);
                  setUploadDebug(null);
                  setUploadDetails(null);
                  setPendingDetailsFile(null);
                  if (user) clearPendingUpload(user._id || user.id || user.email, "lesson_review");

                }}
                className="mt-5 max-w-md mx-auto"
                testIdPrefix="lr-upload"
              />
            )}
            {uploadPhase !== "idle" && (
              <UploadDebugPanel
                debug={uploadDebug}
                file={uploadFile || pausedUpload?.file || null}
                paused={pausedUpload}
              />
            )}
          </section>
          )}
          </>
        )}

        {/* ACTIVE PIPELINE */}
        {active && (
          <ActiveReview
            review={active}
            onReset={() => { setActive(null); }}
            onDelete={deleteReview}
            onContinuePayment={() => setPricingOpen(true)}
            truth={truth}
          />
        )}

        {/* HISTORY */}
        {history.length > 0 && (
          <section data-testid="lr-history">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-3">
              Previous reviews · {history.length}
            </div>
            <div className="space-y-2.5">
              {history.map((r) => {
                const rowAwaiting = r.status === "awaiting_payment" && !r.paid;
                return (
                <div
                  key={r.id}
                  className={`w-full rounded-2xl border bg-white/[0.025] p-4 transition group ${
                    rowAwaiting
                      ? "border-[#D4AF37]/40"
                      : "border-white/[0.07] hover:border-[#D4AF37]/30"
                  }`}
                >
                  <button
                    type="button"
                    data-testid={`lr-history-${r.id}`}
                    onClick={() => openDetail(r.id)}
                    className="w-full text-left flex items-center gap-3"
                  >
                    <div className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/[0.06] flex items-center justify-center flex-shrink-0">
                      <FileVideo className="w-4 h-4 text-zinc-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        data-testid={`lr-history-title-${r.id}`}
                        className="text-sm font-semibold text-white truncate"
                      >
                        {r.display_title || r.filename || "Lesson"}
                      </div>
                      {r.camera_label && (
                        <div
                          data-testid={`lr-history-camera-${r.id}`}
                          className="text-[11px] text-zinc-400 mt-0.5 truncate"
                        >
                          {r.camera_label}
                        </div>
                      )}
                      <div className="text-[11.5px] text-zinc-500 mt-0.5 flex flex-wrap items-center gap-2">
                        <span>{new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                        {r.duration_seconds ? <><span className="text-zinc-700">·</span><span>{Math.round(r.duration_seconds / 60)} min</span></> : null}
                        <span className="text-zinc-700">·</span>
                        <StatusPill status={r.status} />
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-[#D4AF37] transition" />
                  </button>
                  {rowAwaiting && (
                    <div className="mt-3 pt-3 border-t border-white/[0.07] flex items-center gap-2">
                      <button
                        type="button"
                        data-testid={`lr-history-pay-${r.id}`}
                        onClick={(e) => { e.stopPropagation(); startPayment(r); }}
                        className="flex-1 h-9 px-3 rounded-md bg-gradient-to-br from-[#F3E5AB] to-[#D4AF37] hover:brightness-110 text-black text-[12px] font-extrabold"
                      >
                        Pay for review
                      </button>
                      <button
                        type="button"
                        data-testid={`lr-history-discard-${r.id}`}
                        onClick={(e) => { e.stopPropagation(); deleteReview(r.id); }}
                        className="h-9 px-3 rounded-md bg-transparent border border-white/15 text-zinc-300 text-[12px] font-semibold hover:bg-white/5"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {open && (
        <ReviewDetailDrawer
          doc={openDoc}
          isAdmin={(user?.role || "").toLowerCase() === "admin"}
          onClose={() => { setOpen(null); setOpenDoc(null); }}
          onDelete={() => deleteReview(open)}
          onPay={(r) => startPayment(r || openDoc)}
          truth={truth}
        />
      )}

      <UploadPricingModal
        open={pricingOpen}
        review={active}
        quote={pricingQuote}
        founderFree={founderFree}
        onClose={() => setPricingOpen(false)}
        onPaid={(updated) => { setActive(updated); setPricingOpen(false); }}
      />

      {basketOpen && useV2Basket && (
        <ClipBasketV2
          user={user}
          initialIntent={null}
          onClose={() => {
            setBasketOpen(false);
            setResumeBasketId(null);
          }}
          onHandedOff={async (basketId) => {
            setBasketOpen(false);
            setResumeBasketId(null);
            setResumeBasket(null);
            // Post-handoff active-review pointer — same "Never ghost
            // the user" guarantee as the legacy path. v2 owns the
            // pre-handoff lifecycle; after the user confirms analysis,
            // the parent shows the in-flight review.
            try {
              await loadHistory();
              const { data } = await axios.get(
                `${API}/api/lesson-review/list`,
                { withCredentials: true },
              );
              const items = data?.items || [];
              const inFlight = items.find((it) => {
                const s = String(it.status || "").toLowerCase();
                return s && s !== "complete" && s !== "completed" && s !== "failed";
              });
              if (inFlight) {
                setActive(inFlight);
                try {
                  if (user) {
                    const uid = user._id || user.id || user.email;
                    rememberRecentReview({
                      userId: uid,
                      reviewId: inFlight.id,
                      status: inFlight.status,
                      fileName: null,
                      fileSize: null,
                      role: user?.role || null,
                      reviewType: "clip_basket_v2",
                    });
                  }
                } catch (_) { /* never break navigation on storage */ }
                try {
                  window.history.replaceState(
                    {},
                    "",
                    `${window.location.pathname}?id=${inFlight.id}`,
                  );
                } catch (_) { /* noop */ }
              }
            } catch { /* silent */ }
          }}
        />
      )}

      {basketOpen && !useV2Basket && (
        <ClipBasketModal
          requestId={null}
          selfReview
          pupilName={null}
          focusArea={null}
          focusOther={null}
          timeRange={null}
          beaconSource="lesson_review"
          resumeBasketId={resumeBasketId}
          onClose={() => {
            setBasketOpen(false);
            setResumeBasketId(null);
          }}
          onHandedOff={async (basketId) => {
            setBasketOpen(false);
            setResumeBasketId(null);
            setResumeBasket(null);
            // P0 May 20, 2026 — operational continuity fix. Previously
            // we only refreshed history and showed a 9s toast, leaving
            // the user on the empty start screen — which felt like the
            // upload had vanished. Now we refresh the list AND set the
            // most recently-created processing review as `active` so
            // the page switches to the in-flight view automatically.
            // The PendingAnalysisRail above also catches anything we
            // miss here. Doctrine: "Never ghost the user."
            try {
              await loadHistory();
              const { data } = await axios.get(
                `${API}/api/lesson-review/list`,
                { withCredentials: true },
              );
              const items = data?.items || [];
              // Best-effort: find the freshest in-flight review. The
              // basket_id is not directly indexed on lesson_reviews,
              // so we take the most recently-created item that is
              // still in flight (queued / transcribing / etc).
              const inFlight = items.find((it) => {
                const s = String(it.status || "").toLowerCase();
                return s && s !== "complete" && s !== "completed" && s !== "failed";
              });
              if (inFlight) {
                setActive(inFlight);
                // ── P0 May 20, 2026 — durability repair (Change 3).
                // Sticky review pointer for the basket → review
                // handoff. Mirrors the OLD single-clip path's
                // `rememberRecentReview` on chunked-complete: a
                // reload during the "basket processing" stage will
                // route back to this review via the recent-review
                // recovery pointer instead of dropping the user on
                // an empty start screen.
                try {
                  if (user) {
                    const uid = user._id || user.id || user.email;
                    rememberRecentReview({
                      userId: uid,
                      reviewId: inFlight.id,
                      status: inFlight.status,
                      fileName: null,
                      fileSize: null,
                      role: user?.role || null,
                      reviewType: "clip_basket",
                    });
                  }
                } catch (_) { /* never break navigation on storage */ }
                try {
                  window.history.replaceState(
                    {},
                    "",
                    `${window.location.pathname}?id=${inFlight.id}`,
                  );
                } catch (_) { /* noop */ }
              }
            } catch { /* silent */ }
            truth.setState("completed", 
              "Multi-clip lesson started — analysing now",
              { duration: 5000 },
            );
          }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* ACTIVE REVIEW — shows pipeline + result inline                      */
/* ─────────────────────────────────────────────────────────────────── */

function ActiveReview({ review, onReset, onDelete, onContinuePayment, truth }) {
  const stage = STAGE_LABELS[review.status] || STAGE_LABELS.queued;
  const Icon = stage.icon;
  const done = review.status === "complete";
  const failed = review.status === "failed";
  const clipReady = review.status === "clip_ready";
  // Two-flag payment truth: status alone can lie (manual DB flip leaves
  // status=awaiting_payment with paid=True). Trust both fields.
  const awaitingPayment = review.status === "awaiting_payment" && !review.paid;
  // Active (paid & in-flight) → use the full analysis-progress experience.
  const inFlight = !done && !failed && !awaitingPayment && !clipReady;

  return (
    <section data-testid="lr-active" className="rounded-3xl border border-white/[0.07] bg-white/[0.025] p-5 md:p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
          failed ? "bg-rose-500/15 border border-rose-500/30"
                 : done ? "bg-emerald-500/15 border border-emerald-500/30"
                        : "bg-[#D4AF37]/15 border border-[#D4AF37]/30"
        }`}>
          <Icon className={`w-5 h-5 ${
            failed ? "text-rose-400" : done ? "text-emerald-400" : "text-[#D4AF37]"
          } ${!done && !failed ? "animate-pulse" : ""}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div
            data-testid="lr-active-title"
            className="text-sm font-semibold text-white truncate"
          >
            {review.display_title || review.filename || "Lesson review"}
          </div>
          {review.camera_label && (
            <div
              data-testid="lr-active-camera"
              className="text-[11px] text-zinc-400 truncate"
            >
              {review.camera_label}
            </div>
          )}
          <div className="text-[11.5px] text-zinc-500">
            {failed ? "Processing failed" : done ? "Analysis ready" : `Working — ${stage.label.toLowerCase()}…`}
          </div>
        </div>
        <button data-testid="lr-active-close" onClick={onReset} className="p-2 rounded-lg hover:bg-white/5">
          <X className="w-4 h-4 text-zinc-400" />
        </button>
      </div>

      {inFlight && (
        <AIAnalysisProgress
          status={review.status}
          startedAt={review.paid_at || review.started_at || review.created_at}
        />
      )}

      {clipReady && (
        <ClipReadyPanel
          review={review}
          onConfirmed={() => { /* polling will pick up grading status */ }}
        />
      )}

      {awaitingPayment && (
        <div data-testid="lr-awaiting-payment" className="rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4 text-[13px] text-zinc-200">
          <p className="font-bold text-white mb-1">Ready for AI processing</p>
          <p className="text-[12px] text-zinc-400 mb-3">Pick a review option to start analysis.</p>
          <div className="flex gap-2">
            <Button
              data-testid="lr-awaiting-continue"
              onClick={onContinuePayment}
              className="h-10 px-4 bg-gradient-to-br from-[#F3E5AB] to-[#D4AF37] hover:brightness-110 text-black text-[12px] font-extrabold rounded-md"
            >
              Choose an option
            </Button>
            <Button
              data-testid="lr-awaiting-delete"
              onClick={() => onDelete(review.id)}
              variant="outline"
              className="h-10 px-3 bg-transparent border border-white/15 text-zinc-300 text-[12px] font-semibold rounded-md"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Discard upload
            </Button>
          </div>
        </div>
      )}

      {failed && (
        <AIAnalysisProgress
          failed
          errorMessage={review.error}
          onRetry={onReset}
          onContactSupport={() => {
            const mailto = `mailto:support@nationaldrivingguild.co.uk?subject=AI%20analysis%20failed%20(review%20${encodeURIComponent(review.id || "")})&body=My%20analysis%20failed.%20Please%20help.`;
            window.location.href = mailto;
          }}
        />
      )}

      {done && <ReviewResult doc={review} truth={truth} />}
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* RESULT — competencies + next focus + transcript                     */
/* ─────────────────────────────────────────────────────────────────── */

/**
 * Playable video + clip timeline + download/share controls.
 * Streams from /api/lesson-review/{id}/video (HTTP Range; auth-gated).
 *
 * For multi-clip basket reviews (uploaded_via=clip_basket) we render
 * an ordered "clips in this session" strip beneath the player so the
 * operator can see the lesson's structural arc. Click a chip to seek.
 */
function ReviewVideoPlayer({ doc, truth }) {
  const videoRef = useRef(null);
  const [error, setError] = useState(false);
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const isBasket = doc.uploaded_via === "clip_basket";
  const clips = isBasket
    ? (doc.session_clips || []).slice().sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0),
      )
    : [];

  // Cumulative seek offsets so a click on "Clip 3" seeks to the
  // start of clip 3 in the stitched video. (Feb 11, 2026 hot-fix:
  // when one or more clips lack `duration_seconds` — typically
  // because ffprobe failed at upload time — the naive cumulative
  // sum collapsed to [0,0,0,0] and every clip card seeked to 0,
  // making the user think all cards were misbound to clip 1.
  // Now: any zero in the offsets array gets back-filled by an
  // even-split estimate against the stitched video's totalDuration
  // (read live from the player) so seeks remain useful even with
  // missing per-clip durations.
  const [stitchedDuration, setStitchedDuration] = useState(0);
  const offsets = (() => {
    const out = [0];
    for (let i = 0; i < clips.length - 1; i++) {
      const dur = Number(clips[i].duration_seconds) || 0;
      out.push(out[i] + dur);
    }
    // If the cumulative sum stays at 0 past the first slot, the
    // per-clip durations are missing. Fall back to an even split
    // across the live video duration.
    const summedDur = clips.reduce(
      (a, c) => a + (Number(c.duration_seconds) || 0), 0,
    );
    if (summedDur < 1 && stitchedDuration > 1 && clips.length > 0) {
      const slot = stitchedDuration / clips.length;
      return clips.map((_, i) => slot * i);
    }
    return out;
  })();

  const seekTo = (i) => {
    if (!videoRef.current) return;
    try {
      videoRef.current.currentTime = offsets[i] || 0;
      videoRef.current.play().catch(() => {});
    } catch { /* silent */ }
  };

  const onShare = async () => {
    const url = `${window.location.origin}/lesson-review?id=${doc.id}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: doc.display_title || "Lesson review",
          text: "My lesson review",
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        truth.setState("completed", "Link copied");
      }
    } catch { /* user cancelled */ }
  };

  return (
    <section
      data-testid="lr-result-video"
      className="rounded-2xl border border-white/[0.08] bg-black/40 overflow-hidden"
    >
      {!error ? (
        <video
          ref={videoRef}
          data-testid="lr-result-video-el"
          src={`${apiBase}/api/lesson-review/${doc.id}/video`}
          controls
          preload="metadata"
          playsInline
          crossOrigin="use-credentials"
          onError={() => setError(true)}
          onLoadedMetadata={(e) => {
            // Captured for the missing-duration fallback offsets so
            // each clip card still seeks to its proportional slot
            // even if per-clip ffprobe failed at upload time.
            const d = Number(e?.currentTarget?.duration);
            if (Number.isFinite(d) && d > 0) setStitchedDuration(d);
          }}
          className="w-full max-h-screen-safe bg-black aspect-video"
        />
      ) : (
        <div
          data-testid="lr-result-video-error"
          className="aspect-video flex flex-col items-center justify-center text-zinc-500 p-6"
        >
          <Film className="w-8 h-8 mb-2 opacity-60" />
          <p className="text-[12px]">Video preview unavailable.</p>
          <p className="text-[11px] text-zinc-600 mt-1">
            The original file may have been moved by storage retention.
          </p>
        </div>
      )}

      {/* Clip timeline (basket only) */}
      {isBasket && clips.length > 0 && (
        <div
          data-testid="lr-result-clip-timeline"
          className="px-3 py-3 border-t border-white/[0.06]"
        >
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-2">
            {clips.length} clip{clips.length === 1 ? "" : "s"} in this session
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {clips.map((c, i) => {
              const dur = Number(c.duration_seconds) || 0;
              const mins = dur ? Math.round(dur / 60) : null;
              return (
                <button
                  key={c.id || i}
                  type="button"
                  onClick={() => seekTo(i)}
                  data-testid={`lr-result-clip-${i}`}
                  className="flex-none rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 px-3 py-2 text-left transition"
                >
                  <div className="text-[10.5px] uppercase tracking-widest text-zinc-500 font-bold">
                    Clip {i + 1}
                  </div>
                  <div className="text-[12px] text-white font-semibold truncate max-w-[180px]">
                    {c.camera_label || c.filename || "Clip"}
                  </div>
                  {mins !== null && (
                    <div className="text-[10.5px] text-zinc-500">~{mins} min</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Download / share */}
      <div className="px-3 py-2 border-t border-white/[0.06] flex items-center gap-2 bg-black/30">
        <a
          data-testid="lr-result-download"
          href={`${apiBase}/api/lesson-review/${doc.id}/video`}
          download={`${doc.display_title || doc.id}.${doc.ext || "mp4"}`}
          className="inline-flex items-center gap-1.5 text-[11.5px] font-bold text-zinc-300 hover:text-white px-2 py-1.5 rounded-md hover:bg-white/[0.06] transition"
        >
          <Download className="w-3.5 h-3.5" /> Download video
        </a>
        <button
          type="button"
          data-testid="lr-result-share"
          onClick={onShare}
          className="inline-flex items-center gap-1.5 text-[11.5px] font-bold text-zinc-300 hover:text-white px-2 py-1.5 rounded-md hover:bg-white/[0.06] transition"
        >
          <Share2 className="w-3.5 h-3.5" /> Share link
        </button>
        {doc.duration_seconds ? (
          <span className="ml-auto text-[10.5px] text-zinc-500">
            {Math.round(doc.duration_seconds / 60)} min
          </span>
        ) : null}
      </div>
    </section>
  );
}

function ReviewResult({ doc, truth }) {
  const [showTranscript, setShowTranscript] = useState(false);
  const demonstrated = doc.demonstrated_detailed || doc.demonstrated || [];
  const missed = doc.missed_detailed || doc.missed || [];
  const focus = doc.next_focus || [];
  // Feb 8, 2026 — PDI/ADI self-review readiness layer. Only renders
  // when the backend produced a readiness sub-doc; learner reviews
  // and pupil-paid reviews never see this card (the field isn't
  // written, so this branch is skipped). Role separation is enforced
  // server-side — the UI just trusts the field's presence.
  const readiness = doc.readiness;
  // Feb 11, 2026 — dual-score upgrade. New reviews carry a
  // `dual_score` payload (pupil readiness + 17-competency instructor
  // sheet + per-event evidence). Legacy reviews don't, so we render
  // a small banner explaining the legacy scoring and fall through
  // to the existing demonstrated/missed/focus UI.
  const dualScore = doc.dual_score || null;
  const isLegacy = !dualScore && doc.status === "complete";

  // T8 doctrine (May 19, 2026) — when the backend declares the
  // evidence insufficient, suppress EVERY assessment surface:
  // dual-score, readiness, improvement_tip, competency chips,
  // next-focus, legacy banner. Raw proof (video + transcript) stays
  // visible because that is evidence, not judgement. Doctrine pin:
  // "Raw proof can remain visible. Judgement must be suppressed."
  const evidenceInsufficient =
    (doc.evidence_quality || "").toString().toLowerCase() === "insufficient";

  return (
    <div className="mt-5 space-y-5">
      {/* Playable video + ordered clip list */}
      <ReviewVideoPlayer doc={doc} truth={truth} />

      {/* Track A Phase F (Feb 13, 2026) — Calm "What your instructor
          said" pill. Renders only when the linked ai_offers /confirm
          mirror wrote {status: 'confirmed', decision: ...} onto this
          review. Operator-locked decision copy lives in the pill. */}
      <InstructorConfirmationPill
        confirmation={doc?.instructor_confirmation}
      />

      {/* T8 — Insufficient evidence calm panel. Replaces every
          judgement surface. */}
      {evidenceInsufficient && (
        <InsufficientEvidencePanel
          explanation={doc.evidence_explanation}
          analysisIntent={doc.analysis_intent}
        />
      )}

      {/* Dual-score panel — new reviews only, gated on evidence. */}
      {!evidenceInsufficient && dualScore && (
        <DualScorePanel
          dualScore={dualScore}
          reviewIntent={doc?.review_intent || null}
          onSeek={(ts) => {
            // Wire to the video element. window.lrVideoEl is set in
            // ReviewVideoPlayer's onLoadedMetadata fallback ref.
            try {
              const v = document.querySelector(
                '[data-testid="lr-result-video-el"]',
              );
              if (v && Number.isFinite(ts)) {
                v.currentTime = Math.max(0, ts);
                v.play().catch(() => {});
              }
            } catch (_) { /* noop */ }
          }}
        />
      )}

      {/* Legacy banner — gated on evidence. */}
      {!evidenceInsufficient && isLegacy && (
        <div
          data-testid="legacy-scoring-banner"
          className="rounded-xl border border-amber-700/40 bg-amber-950/30 p-3 text-[12px] text-amber-200"
        >
          <p className="font-extrabold uppercase tracking-widest text-amber-300 text-[10px] mb-1">
            Scored with legacy system
          </p>
          New dual scoring (Pupil Readiness + 17-competency Instructor)
          applies to reviews from Feb 11, 2026 onwards. This review
          was analysed before the upgrade — strengths, weaknesses and
          action plan are shown below.
        </div>
      )}

      {/* Readiness card — PDI/ADI self-reviews only, gated on evidence. */}
      {!evidenceInsufficient && readiness && <ReadinessCard readiness={readiness} />}

      {/* Improvement tip — gated on evidence. */}
      {!evidenceInsufficient && doc.improvement_tip && (
        <div data-testid="lr-improvement-tip" className="rounded-2xl border border-[#D4AF37]/35 bg-[#D4AF37]/[0.06] p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold flex items-center gap-2 mb-2">
            <Sparkles className="w-3 h-3" /> Headline
          </div>
          <p className="text-[14px] text-white leading-relaxed">{doc.improvement_tip}</p>
        </div>
      )}

      {/* Competency chips — gated on evidence. */}
      {!evidenceInsufficient && (
        <div className="grid sm:grid-cols-2 gap-4">
          <CompetencyColumn data-testid="lr-demonstrated" title="Demonstrated" items={demonstrated} positive />
          <CompetencyColumn data-testid="lr-missed" title="Missed or weak" items={missed} />
        </div>
      )}

      {/* Top 3 next focus — gated on evidence. */}
      {!evidenceInsufficient && focus.length > 0 && (
        <div data-testid="lr-next-focus">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-3 flex items-center gap-2">
            <Target className="w-3 h-3" /> Top 3 Next Focus
          </div>
          <div className="space-y-2.5">
            {focus.map((p, i) => (
              <a
                key={i}
                href={p.mastery_skill_url}
                onClick={() => trackFunnel("practice_review", "focus_clicked", { rank: i + 1, mastery_skill_id: p.mastery_skill_id })}
                data-testid={`lr-focus-${i}`}
                className="flex items-start gap-3 rounded-2xl border border-white/[0.07] bg-white/[0.025] hover:border-[#D4AF37]/35 p-4 transition"
              >
                <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0 text-[12px] font-bold text-[#F3E5AB]">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-semibold text-white">{p.mastery_skill_title}</div>
                  <div className="text-[11.5px] text-zinc-400 mt-0.5">{p.mastery_skill_summary}</div>
                  <div className="text-[10.5px] text-zinc-500 mt-1">
                    Triggered by: <span className="text-zinc-300">{p.competency_label}</span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-600 flex-shrink-0 mt-1" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Transcript excerpt */}
      {doc.transcript && (
        <div data-testid="lr-transcript">
          <button
            data-testid="lr-transcript-toggle"
            onClick={() => setShowTranscript((s) => !s)}
            className="text-[12px] text-zinc-400 hover:text-white inline-flex items-center gap-1.5 transition"
          >
            <Play className={`w-3 h-3 transition-transform ${showTranscript ? "rotate-90" : ""}`} />
            {showTranscript ? "Hide transcript" : "Show transcript"}
          </button>
          {showTranscript && (
            <pre className="mt-3 max-h-72 overflow-auto rounded-xl border border-white/[0.07] bg-black/40 p-4 text-[12.5px] text-zinc-300 leading-relaxed whitespace-pre-wrap font-sans">
              {doc.transcript}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

function CompetencyColumn({ title, items, positive, ...rest }) {
  return (
    <div {...rest} className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-4">
      <div className={`text-[10.5px] uppercase tracking-[0.22em] font-bold mb-3 flex items-center gap-2 ${positive ? "text-emerald-300" : "text-rose-300"}`}>
        {positive ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
        {title} · {items.length}
      </div>
      {items.length === 0 ? (
        <p className="text-[12px] text-zinc-500 italic">None detected this session.</p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((c, i) => (
            <li key={i} className="text-[12.5px] text-zinc-200 leading-snug flex gap-2">
              <span className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${positive ? "bg-emerald-400" : "bg-rose-400"}`} />
              <span>
                {typeof c === "string" ? c : c.label}
                {typeof c !== "string" && c.category && (
                  <span className="block text-[10.5px] text-zinc-500 mt-0.5">{c.category}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* READINESS CARD — PDI/ADI self-reviews only (Feb 8, 2026)            */
/* ─────────────────────────────────────────────────────────────────── */

const BAND_TONE = {
  "Emerging":                     { dot: "bg-zinc-400",    text: "text-zinc-200",   accent: "border-zinc-500/40 bg-zinc-500/10" },
  "Developing":                   { dot: "bg-sky-400",     text: "text-sky-200",    accent: "border-sky-500/40 bg-sky-500/10" },
  "Near test standard":           { dot: "bg-amber-400",   text: "text-amber-200",  accent: "border-amber-500/40 bg-amber-500/10" },
  "Test-ready with refinement":   { dot: "bg-emerald-400", text: "text-emerald-200", accent: "border-emerald-500/40 bg-emerald-500/10" },
  "Strong instructional standard":{ dot: "bg-[#D4AF37]",   text: "text-[#F3E5AB]",  accent: "border-[#D4AF37]/45 bg-[#D4AF37]/10" },
};

function ReadinessCard({ readiness }) {
  const r = readiness || {};
  const tone = BAND_TONE[r.band] || BAND_TONE["Developing"];
  const score = r.score ?? 0;
  const max = r.max_score ?? 51;
  const pct = max > 0 ? Math.min(100, Math.round((score / max) * 100)) : 0;
  const blockers = r.pass_blockers || [];
  const strongest = r.strongest_competencies || [];
  const weakest = r.weakest_competencies || [];

  return (
    <div
      data-testid="lr-readiness-card"
      data-readiness-band={r.band}
      className={`rounded-2xl border p-5 ${tone.accent}`}
    >
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold flex items-center gap-2 mb-3 text-[#D4AF37]">
        <Sparkles className="w-3 h-3" /> Estimated readiness
      </div>

      {/* Score + band */}
      <div className="flex items-end justify-between gap-4 mb-4">
        <div>
          <div data-testid="lr-readiness-score" className={`text-[44px] font-bold leading-none ${tone.text}`}>
            {score}<span className="text-[22px] text-zinc-400">/{max}</span>
          </div>
          <div className="mt-1.5 flex items-center gap-2 text-[12px] text-zinc-300">
            <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
            <span data-testid="lr-readiness-band" className={`font-semibold ${tone.text}`}>{r.band}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">AI-estimated</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">training readiness</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 w-full rounded-full bg-white/[0.06] overflow-hidden mb-4">
        <div className={`h-full ${tone.dot}`} style={{ width: `${pct}%` }} />
      </div>

      {/* Reinforcement summary */}
      {r.reinforcement_summary && (
        <p data-testid="lr-readiness-summary" className="text-[13px] text-zinc-200 leading-relaxed mb-4">
          {r.reinforcement_summary}
        </p>
      )}

      {/* Strongest / weakest competencies */}
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <div data-testid="lr-readiness-strongest">
          <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-bold mb-1.5">Strongest</div>
          <ul className="space-y-1">
            {strongest.map((c) => (
              <li key={c.id} className="text-[12px] text-zinc-200 flex items-start gap-2">
                <span className="mt-1 w-1 h-1 rounded-full bg-emerald-400 flex-shrink-0" />
                <span>{c.label} <span className="text-zinc-500">· {c.score}/3</span></span>
              </li>
            ))}
          </ul>
        </div>
        <div data-testid="lr-readiness-weakest">
          <div className="text-[10px] uppercase tracking-[0.18em] text-rose-300 font-bold mb-1.5">Refine next</div>
          <ul className="space-y-1">
            {weakest.map((c) => (
              <li key={c.id} className="text-[12px] text-zinc-200 flex items-start gap-2">
                <span className="mt-1 w-1 h-1 rounded-full bg-rose-400 flex-shrink-0" />
                <span>{c.label} <span className="text-zinc-500">· {c.score}/3</span></span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Pass blockers — only if any */}
      {blockers.length > 0 && (
        <div data-testid="lr-readiness-blockers" className="rounded-xl border border-rose-500/30 bg-rose-500/[0.06] p-3 mb-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-rose-300 font-bold mb-1.5">Highest-impact areas</div>
          <ul className="space-y-1">
            {blockers.map((c) => (
              <li key={c.id} className="text-[12px] text-zinc-200 flex items-start gap-2">
                <span className="mt-1 w-1 h-1 rounded-full bg-rose-400 flex-shrink-0" />
                <span>{c.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Disclaimer — non-negotiable, never hide */}
      <p data-testid="lr-readiness-disclaimer" className="text-[10.5px] text-zinc-500 italic leading-snug">
        {r.disclaimer || "This is guidance only and not a guaranteed DVSA result."}
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* HISTORY DETAIL DRAWER                                               */
/* ─────────────────────────────────────────────────────────────────── */

function ReviewDetailDrawer({ doc, isAdmin = false, onClose, onDelete, onPay, truth }) {
  const [retrying, setRetrying] = useState(false);
  // Two-flag truth — status alone can lie.
  const isAwaitingPayment =
    doc?.status === "awaiting_payment" && !doc?.paid;
  const onRetry = async () => {
    if (retrying || !doc?.id) return;
    // Awaiting payment? Don't retry — open checkout.
    if (isAwaitingPayment) {
      onPay?.(doc);
      return;
    }
    setRetrying(true);
    try {
      const { data } = await axios.post(
        `${API}/api/upload-pricing/admin/retry/${doc.id}`,
        {},
        { withCredentials: true }
      );
      if (data.action === "pipeline_kicked") {
        truth.setState("waiting", "Retry kicked — AI processing restarted (this may take a few minutes).");
      } else if (data.action === "source_missing") {
        truth.setState("failed", "Source file missing — please re-upload the lesson.");
      } else if (data.action === "not_paid") {
        truth.setState("failed", "Review isn't marked paid yet — open the review and Pay first.");
      } else if (data.action === "not_found") {
        truth.setState("failed", "Review not found.");
      } else {
        truth.setState("waiting", `Retry: ${data.action}`);
      }
      onClose?.();
    } catch (e) {
      truth.setState("failed", 
        e.response?.data?.detail || "Retry failed — admin only."
      );
    } finally {
      setRetrying(false);
    }
  };

  // Production bug fix (May 14, 2026 — ticket: "Retry failed —
  // admin only" toast appeared for non-admins while a review was
  // in `downloading` status). Two issues compounded:
  //   1. The previous gate was a NEGATIVE whitelist (`status !==
  //      complete && status !== transcribing && ...`) which let
  //      Retry render for every other status including the
  //      pipeline-active set (downloading, grading,
  //      extracting_clips, preparing_release, queued).
  //   2. The retry endpoint here is `/admin/retry/{id}` — admin-
  //      only by design. Non-admins should never see this button.
  //
  // Canonical pipeline-active set (mirrors lib/recentReviewRecovery
  // and the backend `/api/lesson-review/interrupted` whitelist).
  const PIPELINE_ACTIVE_STATUSES = new Set([
    "pending", "queued", "downloading", "transcribing",
    "extracting_audio", "extracting_clips", "grading",
    "preparing_release", "processing",
  ]);
  const isPipelineActive = PIPELINE_ACTIVE_STATUSES.has(
    String(doc?.status || "").toLowerCase(),
  );
  // Positive whitelist — only user-recoverable terminal failure
  // states surface Retry. `error` is included for legacy rows
  // emitted by the older pipeline.
  const RETRY_ELIGIBLE_STATUSES = new Set([
    "failed", "cancelled", "error",
  ]);
  const isRetryEligible = RETRY_ELIGIBLE_STATUSES.has(
    String(doc?.status || "").toLowerCase(),
  );
  // And: this drawer's retry endpoint is admin-only — render only
  // for admins. Non-admin operational recovery flows live in the
  // Active Operation pill / Operations history surfaces.
  const canRetry = (
    doc
    && !isAwaitingPayment
    && !isPipelineActive
    && isRetryEligible
    && isAdmin
  );

  return (
    <MobileDialog
      bareContent
      open
      onClose={onClose}
      testid="lr-detail-drawer-wrap"
      priority="urgent"
    >
      <div
        data-testid="lr-detail-drawer"
        className="w-full sm:max-w-2xl bg-[#0a0a0a] border-t sm:border sm:rounded-3xl border-white/[0.08] p-5 md:p-7"
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 font-bold">Review</div>
            <div
              data-testid="lr-detail-title"
              className="text-base font-semibold text-white truncate"
            >
              {doc?.display_title || doc?.filename || "Loading…"}
            </div>
            {doc?.camera_label && (
              <div
                data-testid="lr-detail-camera"
                className="text-[11.5px] text-zinc-400 mt-0.5 truncate"
              >
                {doc.camera_label}
              </div>
            )}
            {doc?.notes && (
              <div
                data-testid="lr-detail-notes"
                className="text-[11.5px] text-zinc-300/80 mt-1 leading-snug whitespace-pre-wrap"
              >
                {doc.notes}
              </div>
            )}
          </div>
          {isAwaitingPayment && (
            <button
              data-testid="lr-detail-pay"
              onClick={() => onPay?.(doc)}
              className="px-3 py-1.5 rounded-lg bg-gradient-to-br from-[#F3E5AB] to-[#D4AF37] hover:brightness-110 text-black text-[12px] font-extrabold"
            >
              Pay for review
            </button>
          )}
          {canRetry && (
            <button
              data-testid="lr-detail-retry"
              onClick={onRetry}
              disabled={retrying}
              className="px-3 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-200 text-[12px] font-bold hover:bg-amber-500/25 disabled:opacity-50"
            >
              {retrying ? "Retrying…" : "Retry"}
            </button>
          )}
          <button data-testid="lr-detail-delete" onClick={onDelete} className="p-2 rounded-lg hover:bg-white/5 text-zinc-400 hover:text-rose-300">
            <Trash2 className="w-4 h-4" />
          </button>
          <button data-testid="lr-detail-close" onClick={onClose} className="p-2 rounded-lg hover:bg-white/5">
            <X className="w-4 h-4 text-zinc-400" />
          </button>
        </div>

        {!doc ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>
        ) : isAwaitingPayment ? (
          <div data-testid="lr-detail-awaiting-payment" className="rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4 text-[13px] text-zinc-200 space-y-3">
            <div>
              <p className="font-bold text-white mb-1">Awaiting payment</p>
              <p className="text-[12px] text-zinc-400">
                Pay to start AI analysis. Your upload is safe and will begin processing the moment payment is confirmed.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="lr-detail-pay-inline"
                onClick={() => onPay?.(doc)}
                className="h-10 px-4 rounded-md bg-gradient-to-br from-[#F3E5AB] to-[#D4AF37] hover:brightness-110 text-black text-[12px] font-extrabold"
              >
                Pay for review
              </button>
              <button
                data-testid="lr-detail-discard"
                onClick={onDelete}
                className="h-10 px-3 rounded-md bg-transparent border border-white/15 text-zinc-300 text-[12px] font-semibold hover:bg-white/5"
              >
                Discard upload
              </button>
            </div>
          </div>
        ) : doc.status === "failed" ? (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-[13px] text-rose-200 space-y-2">
            <div className="font-bold text-rose-100">Processing failed</div>
            <div className="whitespace-pre-wrap">{doc.error || "No error detail recorded."}</div>
            <div className="text-rose-200/70 text-[11px]">
              {isAdmin
                ? <>Tap <strong>Retry</strong> above to re-run this analysis from the start.</>
                : <>Our team has been logged in-app. You can discard this upload and try again from a fresh recording.</>}
            </div>
          </div>
        ) : doc.status !== "complete" && doc.status !== "completed" ? (
          <div className="text-[13px] text-zinc-300">
            Still processing — current stage: <strong className="text-amber-200">{STAGE_LABELS[doc.status]?.label || doc.status}</strong>. Close and re-open in a moment.
          </div>
        ) : (
          <ReviewResult doc={doc} truth={truth} />
        )}
      </div>
    </MobileDialog>
  );
}

function StatusPill({ status }) {
  const map = {
    complete:         "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    failed:           "bg-rose-500/15 text-rose-300 border-rose-500/30",
    queued:           "bg-white/[0.04] text-zinc-400 border-white/10",
    extracting_audio: "bg-[#D4AF37]/12 text-[#F3E5AB] border-[#D4AF37]/30",
    transcribing:     "bg-[#D4AF37]/12 text-[#F3E5AB] border-[#D4AF37]/30",
    analysing:        "bg-[#D4AF37]/12 text-[#F3E5AB] border-[#D4AF37]/30",
  };
  const cls = map[status] || map.queued;
  const label = STAGE_LABELS[status]?.label || status;
  return <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] rounded-full px-2 py-0.5 border ${cls}`}>{label}</span>;
}